// PLCIE-SYNC-MARKER v2 (App.js) — re-touched 2026-05-07 to force Save-to-GitHub
// to ship the Creative Analyzer route + nav-item additions that were silently
// dropped on the prior push. Safe to remove once GitHub origin/main reflects
// lines 45 (import), 292 (nav item), and 4424 (<Route path="/analyzer">).
import { useEffect, useState, useRef, useMemo, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import CreativeEnginePage from "./components/CreativeEngine";
import CustomCreativeSession from "./components/custom_creative_session/CustomCreativeSession";
import { ExperimentsPage, ObservationsPage, HypothesesPage, VeoQueuePage } from "./components/IntelligenceViews";
import OfferCardActions from "./components/OfferCardActions";
import ABHCreativeEngine from "./components/ABHCreativeEngine";
import ImageAdStudio from "./components/ImageAdStudio";
import WinnerRotatorPanel from "./components/WinnerRotatorPanel";
import OfferSearchSelect from "./components/OfferSearchSelect";
import GhostReconRebuilding from "./components/GhostReconRebuilding";
import ErrorBoundary from "./components/ErrorBoundary";
import GhostReconOfferDetail from "./components/GhostReconOfferDetail";
import PipelineBoard from "./components/PipelineBoard";
import CreativeHistory from "./components/CreativeHistory";
import CampaignHealthPage, { CampaignHealthWidget } from "./components/CampaignHealthPage";
import { CampaignPerformanceTab, AudienceSegmentsTab } from "./components/CampaignPerformance";
import PnlDashboard from "./components/PnlDashboard";
import PredictionsDashboard from "./components/PredictionsDashboard";
import { OperatorCoachingProfile, OperatorComparisonWidget } from "./components/OperatorCoaching";
import OperationsPage from "./components/OperationsPage";
import IntegrationHealthWidget from "./components/IntegrationHealthWidget";
import SchedulerHealthWidget from "./components/SchedulerHealthWidget";
import ScoreFilter from "./components/ScoreFilter";
import CreativeHub from "./components/CreativeHub";
import SearchBar from "./components/SearchBar";
import RecommendationsDashboard from "./components/RecommendationsDashboard";
import MyDayPage from "./components/MyDayPage";
import VelocityDashboard from "./components/VelocityDashboard";
import OnboardingTour from "./components/OnboardingTour";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CreativeIntelligenceDashboard from "./components/CreativeIntelligenceDashboard";
import TinyTimChat from "./components/TinyTimChat";
import TinyTimSettings from "./components/TinyTimSettings";
import { TinyTimContextProvider } from "./components/TinyTimContext";
import ABHIntelPulse from "./components/ABHIntelPulse";
import ABHSystemReadiness from "./components/ABHSystemReadiness";
import { BUProvider, useBU } from "./components/BUContext";
import BUSelectorScreen from "./components/BUSelectorScreen";
import WorkspaceSwitcher from "./components/WorkspaceSwitcher";
import ABHAttributionPage from "./components/ABHAttributionPage";
import ABHAudiencePage from "./components/ABHAudiencePage";
import ABHABTestsPage from "./components/ABHABTestsPage";
import ABHEmailPage from "./components/ABHEmailPage";
import CreativeAnalyzer from "./components/CreativeAnalyzer"; // PLCIE-SYNC-MARKER v1
// Executive Brain — default-OFF, founder-scoped surface behind REACT_APP_EXEC_BRAIN_ENABLED.
// When the flag is off, none of these routes register and the existing app is byte-identical.
import { EXEC_BRAIN_ENABLED, BrainDashboardLayout, BrainSurface, BrainOverview, DecisionReview, MemoryScreen, OperationsScreen } from "./components/brain";
// Cipher v1.1 shell — default-OFF behind REACT_APP_CIPHER_V2, same pattern.
// This is the ONLY line this migration adds to App.js: every v1.1 route is
// declared in src/shell/V2Routes.jsx so five parallel sessions never reopen
// this file. Off => /v2/* is not registered and this application is unchanged.
import { V2_ENABLED, V2Routes } from "./shell";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ============================================================================
// AUTH CONTEXT
// ============================================================================

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// AUTH CALLBACK
// ============================================================================

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];

      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId,
        });
        setUser(response.data);
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        const errorMsg = error.response?.data?.detail || "Authentication failed";
        navigate("/login", { replace: true, state: { error: errorMsg } });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
        <p className="text-slate-400 mt-4">Authenticating...</p>
      </div>
    </div>
  );
};

// ============================================================================
// PROTECTED ROUTE
// ============================================================================

const ProtectedRoute = ({ children, requireFounder = false, requireFounderOrBuyer = false, requireFounderOrMediaBuyer = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  // BU guard — after login, any multi-BU operator without a selection is sent
  // to /select-bu. Single-BU operators auto-resolve and bypass the screen.
  const bu = useBU();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireFounder && user.role !== "FOUNDER") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireFounderOrBuyer && !["FOUNDER", "MEDIA_BUYER", "OPERATOR"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Strict: Founder + Media Buyer only (NOT generic Operator).
  if (requireFounderOrMediaBuyer && !["FOUNDER", "MEDIA_BUYER"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (bu.needsSelection && location.pathname !== "/select-bu") {
    return <Navigate to="/select-bu" replace />;
  }

  return children;
};

// ============================================================================
// GOOGLE SIGN-IN BUTTON (O10-RULED — direct, Cipher-owned Google auth,
// additive alongside the existing Emergent flow below. Uses Google
// Identity Services' own script + button renderer, not a redirect through
// any Emergent page. Degrades gracefully (renders nothing) if
// REACT_APP_GOOGLE_CLIENT_ID is unset, which it will be until the Founder
// creates one in Google Cloud Console.)
// ============================================================================

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleSignInButton = ({ onToken, onError }) => {
  const buttonRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // nothing to initialize — see graceful-degrade note above
    if (window.google?.accounts?.id) {
      setScriptReady(true);
      return;
    }
    const existing = document.getElementById("google-identity-services");
    if (existing) {
      existing.addEventListener("load", () => setScriptReady(true), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = "google-identity-services";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    script.onerror = () => onError?.("Could not load Google Sign-In");
    document.head.appendChild(script);
  }, [onError]);

  useEffect(() => {
    if (!scriptReady || !GOOGLE_CLIENT_ID || !buttonRef.current) return;
    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (credentialResponse) => onToken(credentialResponse.credential),
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        width: 336,
        text: "signin_with",
      });
    } catch (e) {
      onError?.("Google Sign-In failed to initialize");
    }
  }, [scriptReady, onToken, onError]);

  if (!GOOGLE_CLIENT_ID) {
    // Graceful degrade: no client ID configured yet, so render nothing
    // rather than a broken/crashing button. Never blocks the rest of the
    // login page.
    return null;
  }

  return <div ref={buttonRef} className="flex justify-center" data-testid="google-signin-gis-btn" />;
};

// ============================================================================
// EMAIL + PASSWORD LOGIN FORM (O10-RULED — additive, invited operators only)
// ============================================================================

const PasswordLoginForm = ({ onSuccess, onError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    onError?.(null);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      onSuccess(response.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid email or password";
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="password-login-form">
      <input
        type="email"
        required
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="password-login-email"
        className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-slate-600"
      />
      <input
        type="password"
        required
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="password-login-password"
        className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-slate-600"
      />
      <button
        type="submit"
        disabled={submitting}
        data-testid="password-login-submit"
        className="w-full bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-100 font-semibold py-3 px-4 rounded-lg transition-all"
      >
        {submitting ? "Signing in..." : "Sign in with email"}
      </button>
    </form>
  );
};

// ============================================================================
// LOGIN PAGE
// ============================================================================

const LoginPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const error = location.state?.error;
  const [newAuthError, setNewAuthError] = useState(null);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    navigate("/dashboard", { replace: true, state: { user: userData } });
  };

  const handleGoogleToken = async (idToken) => {
    setNewAuthError(null);
    try {
      const response = await axios.post(`${API}/auth/google`, { id_token: idToken });
      handleAuthSuccess(response.data);
    } catch (err) {
      setNewAuthError(err.response?.data?.detail || "Google sign-in failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4 login-gradient">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-5xl font-extrabold text-white tracking-tight">
            Cipher
          </h1>
          <p className="mt-3 text-slate-400 text-lg">Autonomous Marketing Intelligence</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
            {error}
          </div>
        )}
        {newAuthError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
            {newAuthError}
          </div>
        )}

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-10">
          <p className="text-slate-300 text-center mb-8 text-base">
            Sign in to access your intelligence dashboard.
          </p>

          {/* Cipher-owned Google Sign-In (O10-RULED). The Emergent-proxy
              login this once rendered above has been removed per
              LOGIN-CUTOVER-APPROVED — this and the email/password form
              below are now the only login methods on this page. */}
          <div className="mb-4">
            <GoogleSignInButton onToken={handleGoogleToken} onError={setNewAuthError} />
          </div>

          {/* Cipher-owned email+password login (O10-RULED). */}
          <div className="mb-6">
            <PasswordLoginForm onSuccess={handleAuthSuccess} onError={setNewAuthError} />
          </div>

          <p className="text-slate-500 text-xs text-center mt-4">
            Access is invite-only. Contact the administrator if you need access.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DASHBOARD LAYOUT
// ============================================================================

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bu = useBU();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Base items visible in every workspace
  const navItems = [
    { path: "/my-day", label: "My Day", icon: "calendar" },
    { path: "/dashboard", label: "Dashboard", icon: "grid" },
  ];

  // Executive Brain — additive, flag-gated, founder-scoped "start your day" entry.
  // Flag OFF => absent (existing nav byte-identical). Launches the immersive Brain surface.
  if (EXEC_BRAIN_ENABLED && user?.role === "FOUNDER") {
    navItems.push({ path: "/brain", label: "Brain", icon: "cpu" });
  }

  const isABHWorkspace = bu.currentBU === 'ABH';

  if (!isABHWorkspace) {
    navItems.push(
      { path: "/pipeline", label: "Pipeline", icon: "trending-up" },
      { path: "/ghost-recon", label: "Ghost Recon", icon: "radar" },
      { path: "/creative", label: "Creative", icon: "pen-tool" },
      { path: "/analyzer", label: "Creative Analyzer", icon: "zap" },
      { path: "/campaigns", label: "Campaigns", icon: "zap" },
    );
    // Note: the "Creative" entry above resolves to the Shared Custom Creative
    // Session when REACT_APP_CCS_ENABLED is on, and to the legacy engine when
    // off — see the /creative route. There is no separate CCS nav item; the
    // cutover is transparent to Affiliate operators.
  }

  if (user?.role === "FOUNDER") {
    if (isABHWorkspace) {
      navItems.push(
        { path: "/abh-engine", label: "ABH Engine", icon: "shield" },
        { path: "/abh/attribution", label: "Attribution", icon: "target", indent: true },
        { path: "/abh/audience", label: "Audience", icon: "users", indent: true },
        { path: "/abh/ab-tests", label: "A/B Tests", icon: "bar-chart-2", indent: true },
        { path: "/abh/email", label: "Email", icon: "mail", indent: true },
        { path: "/settings", label: "Settings", icon: "settings" },
      );
    } else {
      navItems.push(
        { path: "/offers", label: "Offers", icon: "package" },
        { path: "/recommendations", label: "Recommendations", icon: "target" },
        { path: "/velocity", label: "Velocity", icon: "bar-chart-2" },
        { path: "/campaign-health", label: "Health", icon: "activity" },
        { path: "/pnl", label: "P&L", icon: "dollar-sign" },
        { path: "/predictions", label: "Predictions", icon: "compass" },
        { path: "/creative-intel", label: "Creative Intel", icon: "eye" },
        { path: "/operators", label: "Operators", icon: "users" },
        { path: "/operations", label: "Operations", icon: "cpu" },
        { path: "/settings", label: "Settings", icon: "settings" },
      );
    }
  } else if (bu.access.includes("ABH")) {
    // ABH operator (e.g. JC) — show ABH Engine directly.
    navItems.push({ path: "/abh-engine", label: "ABH Engine", icon: "shield" });
  }

  // Media buyers get the Offers tab (list + add). Founder-only controls on the
  // page (assignment, operators fetch) remain individually guarded inside the
  // component, so they stay hidden for media buyers.
  if (user?.role === "MEDIA_BUYER") {
    navItems.push({ path: "/offers", label: "Offers", icon: "package" });
  }

  // Intelligence section (additive). Data views are founder-only; the Veo Queue
  // is visible to VIDEO_EDITOR + FOUNDER.
  if (user?.role === "FOUNDER") {
    navItems.push(
      { path: "/intelligence/experiments", label: "Experiments", icon: "flask" },
      { path: "/intelligence/observations", label: "Observations", icon: "eye", indent: true },
      { path: "/intelligence/hypotheses", label: "Hypotheses", icon: "lightbulb", indent: true },
      { path: "/intelligence/veo-queue", label: "Veo Queue", icon: "video", indent: true },
    );
  } else if (user?.role === "VIDEO_EDITOR") {
    navItems.push({ path: "/intelligence/veo-queue", label: "Veo Queue", icon: "video" });
  }

  // Settings is exposed to OPERATOR / MEDIA_BUYER so they can connect their
  // own Meta ad accounts. The Settings page itself hides every founder-only
  // section (BUs, networks, audit log, cost caps, etc) for non-founders.
  if (user?.role && user.role !== "FOUNDER") {
    navItems.push({ path: "/settings", label: "Settings", icon: "settings" });
  }

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-60 bg-slate-900/95 border-r border-slate-800 flex flex-col transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
          <h1 className="text-xl font-extrabold text-white tracking-tight">
            Cipher
          </h1>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden text-slate-400 hover:text-white" data-testid="close-sidebar-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* BU switcher — FOUNDER + multi-BU only (dropdown). Gated inside the component. */}
        <WorkspaceSwitcher user={user} />

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '')}`}
              className={`sidebar-nav-item w-full flex items-center gap-3 text-left ${
                location.pathname === item.path ? "active" : ""
              } ${item.indent ? "pl-8 text-sm text-slate-400" : ""}`}
            >
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-emerald-500/20" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 font-semibold text-sm">
                {user?.name?.[0] || user?.operator_code?.[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 rounded-lg transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto w-full">
        <div className="sticky top-0 z-30 bg-slate-950/80 border-b border-slate-800/50 px-5 md:px-8 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden text-slate-400 hover:text-white flex-shrink-0" data-testid="open-sidebar-btn">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="flex-1 min-w-0"><SearchBar /></div>
        </div>
        <div className="p-5 md:p-8">{children}</div>
      </main>

      {/* Tiny Tim — persistent chat panel */}
      <TinyTimChat user={user} />
    </div>
  );
};

// ============================================================================
// DASHBOARD PAGE
// ============================================================================

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const endpoint = user.role === "FOUNDER" ? "/dashboard/founder" : "/dashboard/operator";
        const response = await axios.get(`${API}${endpoint}`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="dashboard-title">
            {user.role === "FOUNDER" ? "Founder Dashboard" : "Operator Dashboard"}
          </h2>
          <p className="text-slate-400 mt-1">Welcome back, {user.name || user.operator_code}</p>
        </div>

        {user.role === "FOUNDER" ? (
          <FounderDashboard stats={stats} />
        ) : (
          <OperatorDashboard stats={stats} />
        )}
      </div>
    </DashboardLayout>
  );
};

// Creative Engine Pipeline Widget
const CreativeEnginePipelineWidget = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/creative-engine/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch creative engine stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const pipeline = stats.pipeline || {};
  const recent = stats.recent_7_days || {};

  const stages = [
    { 
      id: 'psychology', 
      label: 'Psychology', 
      value: pipeline.psychology?.confirmed || 0,
      pending: pipeline.psychology?.pending || 0,
      color: 'purple'
    },
    { 
      id: 'angles', 
      label: 'Angles', 
      value: pipeline.angles?.selected || 0,
      pending: pipeline.angles?.pending || 0,
      color: 'blue'
    },
    { 
      id: 'scripts', 
      label: 'Scripts', 
      value: pipeline.scripts?.total || 0,
      pending: pipeline.scripts?.with_type || 0,
      color: 'purple'
    },
    { 
      id: 'production', 
      label: 'Production', 
      value: pipeline.production?.in_production || 0,
      pending: pipeline.production?.pending_approval || 0,
      color: 'emerald'
    },
  ];

  const stageColors = {
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="creative-engine-pipeline-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <span>Creative Engine Pipeline</span>
          <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded">Phase 3</span>
        </h3>
        <div className="text-sm text-slate-400">
          {recent.scripts_created > 0 && (
            <span className="text-emerald-400">+{recent.scripts_created} scripts this week</span>
          )}
        </div>
      </div>

      {/* Pipeline Stages */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {stages.map((stage, idx) => (
          <div key={stage.id} className="relative">
            <div className={`p-4 rounded-lg border ${stageColors[stage.color]} text-center`}>
              <div className="text-2xl font-bold">{stage.value}</div>
              <div className="text-xs opacity-80">{stage.label}</div>
              {stage.pending > 0 && stage.id !== 'scripts' && (
                <div className="text-xs text-slate-400 mt-1">
                  {stage.pending} pending
                </div>
              )}
              {stage.id === 'scripts' && stage.pending > 0 && (
                <div className="text-xs text-slate-400 mt-1">
                  {stage.pending} typed
                </div>
              )}
            </div>
            {idx < stages.length - 1 && (
              <div className="absolute top-1/2 -right-2 transform -translate-y-1/2 text-slate-600 text-lg z-10">
                →
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Production Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-700">
        <div className="text-center">
          <div className="text-xl font-bold text-blue-400">{pipeline.production?.pending_approval || 0}</div>
          <div className="text-xs text-slate-400">Pending Approval</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-purple-400">{pipeline.production?.approved || 0}</div>
          <div className="text-xs text-slate-400">Approved</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-amber-400">{pipeline.production?.in_production || 0}</div>
          <div className="text-xs text-slate-400">In Production</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-emerald-400">{pipeline.production?.completed || 0}</div>
          <div className="text-xs text-slate-400">Completed</div>
        </div>
      </div>
    </div>
  );
};

// Ghost Recon Dashboard Widget
const GhostReconWidget = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/ghost-recon/dashboard`);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch Ghost Recon stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="ghost-recon-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Ghost Recon Intelligence</h3>
        <button
          onClick={() => navigate("/ghost-recon")}
          data-testid="ghost-recon-widget-link"
          className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
        >
          View Dashboard →
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
          <div className="text-xl font-bold text-emerald-400">{stats.emerging_count || 0}</div>
          <div className="text-xs text-emerald-400/80">Emerging</div>
        </div>
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
          <div className="text-xl font-bold text-blue-400">{stats.peaking_count || 0}</div>
          <div className="text-xs text-blue-400/80">Peaking</div>
        </div>
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
          <div className="text-xl font-bold text-amber-400">{stats.fading_count || 0}</div>
          <div className="text-xs text-amber-400/80">Fading</div>
        </div>
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
          <div className="text-xl font-bold text-red-400">{stats.anomaly_count || 0}</div>
          <div className="text-xs text-red-400/80">Anomalies</div>
        </div>
      </div>
      {stats.total_offers === 0 && (
        <p className="text-slate-500 text-sm text-center mt-3">No offers tracked yet — run first scan to populate</p>
      )}
      {stats.velocity_leaders?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <p className="text-xs text-slate-500 mb-2">Top Velocity</p>
          {stats.velocity_leaders.slice(0, 3).map((leader, idx) => (
            <div key={leader.offer_id} className="flex items-center justify-between py-1 text-sm">
              <span className="text-slate-300 truncate">{leader.product_name}</span>
              <span className="text-emerald-400 ml-2 shrink-0">{(leader.velocity_score || 0).toFixed(0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FounderDashboard = ({ stats }) => {
  const navigate = useNavigate();
  const { currentBU } = useBU();
  const isABH = currentBU === 'ABH';

  // Agent badge styles
  const getAgentBadge = (agent) => {
    const badges = {
      'aliexpress_trending': { label: 'AliExpress', bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
      'amazon_bsr': { label: 'Amazon', bg: 'bg-purple-500/20', text: 'text-purple-400' },
      'google_trends': { label: 'Trends', bg: 'bg-blue-500/20', text: 'text-blue-400' },
      'reddit_signal': { label: 'Reddit', bg: 'bg-red-500/20', text: 'text-red-400' },
    };
    return badges[agent] || { label: agent || 'Unknown', bg: 'bg-slate-500/20', text: 'text-slate-400' };
  };

  return (
    <div className="space-y-6">
      {/* ABH-first surface — top of page in ABH workspace */}
      {isABH && (
        <>
          <ABHSystemReadiness />
          <ABHIntelPulse />
        </>
      )}

      {/* AFFILIATE dashboard content — hidden in ABH workspace */}
      {!isABH && (
      <>
      {/* Stats Grid - Updated for Phase 2 v2 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          label="Discovered"
          value={stats?.total_discovered || 0}
          color="emerald"
        />
        <StatCard 
          label="Scored" 
          value={stats?.total_scored || 0} 
          color="blue"
          data-testid="scored-counter"
        />
        <StatCard label="Assigned" value={stats?.total_assigned || 0} color="purple" />
        <StatCard label="In Market" value={stats?.total_in_market || 0} color="cyan" />
        <StatCard label="Active Ops" value={stats?.active_operators || 0} color="slate" />
      </div>

      {/* Price Alert if many unconfirmed */}
      {stats?.unconfirmed_price_count > 0 && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <p className="text-purple-400 font-medium">{stats.unconfirmed_price_count} offers have unconfirmed retail prices</p>
          <p className="text-purple-300/70 text-sm mt-1">AliExpress prices are wholesale/COGS. Confirm retail prices before scoring.</p>
        </div>
      )}

      {/* Recent Scans - Updated for v2 discovery agents */}
      {stats?.recent_scans?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Discovery Scans</h3>
          <div className="space-y-2">
            {stats.recent_scans.slice(0, 5).map((scan, idx) => {
              const agentBadge = getAgentBadge(scan.agent);
              return (
                <div
                  key={scan.scan_id || idx}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg text-sm"
                  data-testid={`scan-item-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${agentBadge.bg} ${agentBadge.text}`}>
                      {agentBadge.label}
                    </span>
                    <span className="text-slate-300">
                      {scan.search_term || scan.platform || 'Discovery scan'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-slate-400">
                    <span>{scan.ads_found || scan.products_discovered + scan.products_updated || 0} found</span>
                    <span className="text-emerald-400">+{scan.new_offers || scan.products_discovered || 0} new</span>
                    {scan.errors > 0 && (
                      <span className="text-red-400">{scan.errors} errors</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unassigned Offers */}
      {stats?.unassigned_offers?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Top Unassigned Offers (by score)</h3>
          <div className="space-y-3">
            {stats.unassigned_offers.slice(0, 5).map((offer) => (
              <div
                key={offer.offer_id}
                className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {offer.discovery_source && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        offer.discovery_source.includes('aliexpress') ? 'bg-cyan-500/20 text-cyan-400' :
                        offer.discovery_source.includes('amazon') ? 'bg-amber-500/20 text-amber-400' :
                        offer.discovery_source.includes('trends') ? 'bg-blue-500/20 text-blue-400' :
                        offer.discovery_source.includes('reddit') ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {offer.discovery_source.replace('_agent', '').replace('_trending', '').replace('_bsr', '').replace('_signal', '')}
                      </span>
                    )}
                    {offer.retail_price_unconfirmed && (
                      <span className="px-1.5 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">$ Unconfirmed</span>
                    )}
                  </div>
                  <p className="text-white font-medium">{offer.name}</p>
                  <p className="text-slate-400 text-sm">
                    {offer.retail_price ? `$${offer.retail_price.toFixed(0)} retail` : offer.cogs_estimate ? `~$${offer.cogs_estimate.toFixed(0)} COGS` : ''}
                    {offer.google_trends_velocity && (
                      <span className={`ml-2 ${offer.google_trends_velocity > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {offer.google_trends_velocity > 0 ? '+' : ''}{offer.google_trends_velocity.toFixed(0)}% trend
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {offer.current_score !== null && (
                    <div className={`text-lg font-bold ${offer.current_score >= 70 ? 'text-emerald-400' : offer.current_score >= 50 ? 'text-amber-400' : 'text-slate-400'}`}>
                      {offer.current_score?.toFixed(0)}
                    </div>
                  )}
                  <button
                    onClick={() => navigate(`/offers/${offer.offer_id}`)}
                    className="px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors text-sm"
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Alerts */}
      {stats?.pending_alerts?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Pending Alerts</h3>
          <div className="space-y-3">
            {stats.pending_alerts.map((alert) => (
              <AlertCard key={alert.alert_id} alert={alert} />
            ))}
          </div>
        </div>
      )}

      {/* ABH System Readiness + Intel Pulse render at the top of the page
          when in ABH workspace (see top of FounderDashboard). */}

      {/* Creative Engine Pipeline Widget */}
      <CreativeEnginePipelineWidget />

      {/* Campaign Health Widget */}
      <CampaignHealthWidget />

      {/* Operator Decision Quality Widget */}
      <OperatorComparisonWidget />

      {/* Integration Health Widget */}
      <IntegrationHealthWidget />

      {/* Scheduler Health Widget */}
      <SchedulerHealthWidget />

      {/* Ghost Recon Intelligence Widget */}
      <GhostReconWidget />
      </>
      )}
    </div>
  );
};

const OperatorDashboard = ({ stats }) => {
  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="My Offers" value={stats?.offers_count || 0} color="emerald" />
        <StatCard label="My Campaigns" value={stats?.campaigns_count || 0} color="blue" />
        <StatCard label="Pending Alerts" value={stats?.pending_alerts?.length || 0} color="purple" />
      </div>

      {/* My Offers */}
      {stats?.my_offers?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">My Offers</h3>
          <div className="space-y-3">
            {stats.my_offers.slice(0, 5).map((offer) => (
              <div
                key={offer.offer_id}
                className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg"
              >
                <div>
                  <p className="text-white font-medium">{offer.name}</p>
                  <p className="text-slate-400 text-sm">
                    {offer.offer_code} • {offer.status}
                  </p>
                </div>
                <StatusBadge status={offer.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Alerts */}
      {stats?.pending_alerts?.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Pending Alerts</h3>
          <div className="space-y-3">
            {stats.pending_alerts.map((alert) => (
              <AlertCard key={alert.alert_id} alert={alert} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => {
  const colors = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    cyan: "text-cyan-400",
    slate: "text-slate-300",
    red: "text-red-400",
  };

  return (
    <div className="stat-card-hero min-w-0">
      <p className={`stat-number ${colors[color] || 'text-white'}`}>{value}</p>
      <p className="stat-label text-slate-400">{label}</p>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    // New Phase 2 status flow
    discovered: "bg-blue-500/10 text-blue-400",
    assigned: "bg-amber-500/10 text-amber-400",
    activated: "bg-purple-500/10 text-purple-400",
    in_creative: "bg-pink-500/10 text-pink-400",
    in_market: "bg-emerald-500/10 text-emerald-400",
    closed: "bg-slate-500/10 text-slate-400",
    // Legacy/campaign statuses
    draft: "bg-slate-500/10 text-slate-400",
    pending_review: "bg-purple-500/10 text-purple-400",
    active: "bg-emerald-500/10 text-emerald-400",
    paused: "bg-slate-500/10 text-slate-300",
    killed: "bg-red-500/10 text-red-400",
  };

  const labels = {
    discovered: "Discovered",
    assigned: "Assigned",
    activated: "Activated",
    in_creative: "In Creative",
    in_market: "In Market",
    closed: "Closed",
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status] || colors.discovered}`}>
      {labels[status] || status}
    </span>
  );
};

const AlertCard = ({ alert }) => {
  const severityColors = {
    info: "border-l-blue-500",
    warning: "border-l-amber-500",
    critical: "border-l-red-500",
  };

  return (
    <div className={`p-4 bg-slate-800/50 rounded-lg border-l-4 ${severityColors[alert.severity]}`}>
      <p className="text-white font-medium">{alert.title}</p>
      <p className="text-slate-400 text-sm mt-1">{alert.message}</p>
    </div>
  );
};

// ============================================================================
// PIPELINE PAGE (Updated for Phase 2)
// ============================================================================

const PipelinePage = () => {
  return (
    <DashboardLayout>
      <PipelineBoard />
    </DashboardLayout>
  );
};

// Mirror Python services.creative_naming.clean_offer_name +
// routers.campaigns._sanitize_offer_name so the offer card and the
// backend agree on the OfferName segment shown in the bare campaign-name
// stem (Nick-OfferName-).
const cleanOfferName = (raw) => {
  if (!raw || typeof raw !== "string") return "Unknown";
  const leading = raw.split("—")[0].split("–")[0].trim();
  const stripped = leading.replace(/[^A-Za-z0-9]/g, "");
  return stripped || "Unknown";
};

const PipelineColumn = ({ title, count, offers, showScore = false }) => {
  const navigate = useNavigate();
  // Which offer's bare-stem was just copied (for 2-second visual feedback)
  const [copiedStemId, setCopiedStemId] = useState(null);

  const copyStem = (e, offerId, stem) => {
    e.stopPropagation();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(stem).then(() => {
      setCopiedStemId(offerId);
      setTimeout(() => setCopiedStemId(null), 2000);
    });
  };

  // Discovery source badge styles
  const getSourceBadge = (source) => {
    const badges = {
      'aliexpress_trending_agent': { label: 'AliExpress', bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
      'amazon_bsr_agent': { label: 'Amazon', bg: 'bg-amber-500/20', text: 'text-amber-400' },
      'google_trends_agent': { label: 'Trends', bg: 'bg-blue-500/20', text: 'text-blue-400' },
      'reddit_signal_agent': { label: 'Reddit', bg: 'bg-red-500/20', text: 'text-red-400' },
      'meta': { label: 'Meta', bg: 'bg-blue-600/20', text: 'text-blue-400' },
      'tiktok': { label: 'TikTok', bg: 'bg-pink-500/20', text: 'text-pink-400' },
      'telegram_requested': { label: 'Requested', bg: 'bg-violet-500/20', text: 'text-violet-400' },
    };
    return badges[source] || badges[source?.toLowerCase()] || { label: source || 'Unknown', bg: 'bg-slate-500/20', text: 'text-slate-400' };
  };
  
  // Momentum indicator styles (v2 - named indicators)
  const getMomentumBadge = (indicator, delta) => {
    const badges = {
      'HIGH_RISER': { icon: '↑↑', label: 'High Riser', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
      'RISER': { icon: '↑', label: 'Rising', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      'STABLE': { icon: '→', label: 'Stable', color: 'text-slate-400', bg: 'bg-slate-500/20' },
      'FALLER': { icon: '↓', label: 'Falling', color: 'text-red-400', bg: 'bg-red-500/10' },
      'CLIFF_FALLER': { icon: '↓↓', label: 'Cliff Faller', color: 'text-red-400', bg: 'bg-red-500/20' },
    };
    // Fallback to delta-based if no indicator
    if (!indicator && delta !== null && delta !== undefined) {
      if (delta >= 10) return badges['HIGH_RISER'];
      if (delta >= 3) return badges['RISER'];
      if (delta > -3) return badges['STABLE'];
      if (delta > -10) return badges['FALLER'];
      return badges['CLIFF_FALLER'];
    }
    return badges[indicator] || null;
  };
  
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <h3 className="font-semibold text-white">{title}</h3>
        <span className="px-2 py-1 bg-slate-800 text-slate-400 rounded text-sm">{count}</span>
      </div>
      <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
        {offers.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No offers in {title.toLowerCase()}</p>
        ) : (
          offers.map((offer) => {
            const sourceBadge = getSourceBadge(offer.discovery_source || offer.source_platform);
            const momentumBadge = getMomentumBadge(offer.momentum_indicator, offer.score_delta);
            const hasUnconfirmedPrice = offer.retail_price_unconfirmed;
            
            return (
              <div 
                key={offer.offer_id} 
                className="p-4 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors border border-transparent hover:border-emerald-500/30"
                onClick={() => navigate(`/offers/${offer.offer_id}`)}
                data-testid={`offer-card-${offer.offer_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {/* Discovery Source Badge */}
                      <span className={`px-1.5 py-0.5 rounded text-xs ${sourceBadge.bg} ${sourceBadge.text}`} data-testid="discovery-source-badge">
                        {sourceBadge.label}
                      </span>
                      {hasUnconfirmedPrice && (
                        <span className="px-1.5 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400" data-testid="unconfirmed-price-badge">
                          $ Unconfirmed
                        </span>
                      )}
                    </div>
                    <p className="text-white font-medium">{offer.name}</p>
                    {/* Truncated Description Preview */}
                    {offer.product_description && (
                      <p className="text-slate-500 text-xs mt-1 line-clamp-2" data-testid="description-preview">
                        {offer.product_description.length > 80 
                          ? offer.product_description.substring(0, 80) + '...' 
                          : offer.product_description}
                      </p>
                    )}
                    {!offer.product_description && (
                      <p className="text-slate-400 text-sm mt-1">
                        {offer.advertiser_name || offer.offer_code}
                      </p>
                    )}
                  </div>
                  {showScore && offer.current_score !== null && (
                    <div className="text-right ml-3">
                      <div className={`text-lg font-bold ${offer.current_score >= 70 ? 'text-emerald-400' : offer.current_score >= 50 ? 'text-amber-400' : 'text-slate-400'}`}>
                        {offer.current_score?.toFixed(0)}
                      </div>
                      {/* Momentum Indicator Badge */}
                      {momentumBadge && (
                        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${momentumBadge.bg} ${momentumBadge.color}`} data-testid="momentum-indicator">
                          <span>{momentumBadge.icon}</span>
                          <span>{momentumBadge.label}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Retail Price & Key Stats - Removed CPA */}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3">
                    {offer.retail_price && (
                      <span className="text-emerald-400 font-medium" data-testid="retail-price">
                        ${offer.retail_price.toFixed(0)} retail
                      </span>
                    )}
                    {offer.cogs_estimate && !offer.retail_price && (
                      <span className="text-slate-400 text-sm" data-testid="cogs-estimate">
                        ~${offer.cogs_estimate.toFixed(0)} COGS
                      </span>
                    )}
                    {/* Product URL indicator */}
                    {offer.product_url && (
                      <span className="text-slate-500 text-xs" title={offer.product_url}>
                        Link
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    {offer.ad_running_days && <span>{offer.ad_running_days}d</span>}
                    {offer.google_trends_velocity && (
                      <span className={offer.google_trends_velocity > 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {offer.google_trends_velocity > 0 ? '+' : ''}{offer.google_trends_velocity.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
                {/* Campaign-name stem (bare offer prefix; operator appends initials + tracking) */}
                {offer.name && (() => {
                  const stem = `Nick-${cleanOfferName(offer.name)}-`;
                  const isCopied = copiedStemId === offer.offer_id;
                  return (
                    <div
                      className="mt-2 flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
                      style={{ background: 'rgba(0, 240, 255, 0.04)', border: '1px solid rgba(0, 240, 255, 0.12)' }}
                      data-testid={`offer-card-stem-${offer.offer_id}`}
                    >
                      <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: '#00F0FF', opacity: 0.6 }}>
                        Campaign
                      </span>
                      <code
                        className="text-xs font-mono flex-1 truncate text-white select-all"
                        title="Paste into RedTrack/Meta and append your two-letter initials + any tracking text"
                      >
                        {stem}
                        <span className="text-slate-500 italic">{'<INITIALS>'}</span>
                      </code>
                      <button
                        type="button"
                        onClick={(e) => copyStem(e, offer.offer_id, stem)}
                        className={`px-1.5 py-0.5 rounded text-xs font-mono transition-colors ${
                          isCopied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700 hover:text-white'
                        }`}
                        data-testid={`copy-stem-btn-${offer.offer_id}`}
                      >
                        {isCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  );
                })()}
                {/* AI Brief (Feature 01) */}
                {offer.ollama_brief && (
                  <div className="mt-2 px-2.5 py-2 rounded-lg text-xs leading-relaxed" data-testid="offer-ai-brief"
                    style={{ background: 'rgba(0, 240, 255, 0.04)', border: '1px solid rgba(0, 240, 255, 0.1)', color: '#9CB8BD' }}>
                    <span className="text-[10px] font-mono mr-1" style={{ color: '#00F0FF', opacity: 0.6 }}>AI</span>
                    {offer.ollama_brief.length > 200 ? offer.ollama_brief.substring(0, 200) + '...' : offer.ollama_brief}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================================
// OFFER DETAIL PAGE
// ============================================================================


const CreativeEngineDropdown = ({ offerId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/creative/sessions?offer_id=${offerId}`);
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.debug('Failed to fetch sessions:', e);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleToggle = () => {
    if (!open) fetchSessions();
    setOpen(!open);
  };

  // I6 — was a third local copy of the CCS-cutover branch; now calls the one
  // shared implementation (also used by CreativeActions and CreativeHub).
  const startNewSession = async () => {
    setCreating(true);
    try {
      const res = await startCreativeSessionShared(offerId);
      if (!res.ok) {
        alert(res.error || 'Failed to create session');
        return;
      }
      navigate(res.path);
    } finally {
      setCreating(false);
    }
  };

  const offerSessions = sessions.filter(s => s.offer_id === offerId);

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-2"
        data-testid="creative-engine-btn"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        Creative Engine
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="creative-engine-dropdown">
          <div className="p-3 border-b border-slate-800">
            <button
              onClick={startNewSession}
              disabled={creating}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="new-creative-session-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {creating ? 'Creating...' : 'New Creative Session'}
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loadingSessions ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-emerald-500 mx-auto" />
              </div>
            ) : offerSessions.length === 0 ? (
              <div className="p-4 text-center text-slate-500 text-sm">
                No previous sessions
              </div>
            ) : (
              <div className="p-2 space-y-1">
                <p className="text-slate-500 text-xs px-2 pb-1">Previous Sessions</p>
                {offerSessions.map(session => (
                  <button
                    key={session.session_id}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/creative-engine/${offerId}?session=${session.session_id}`);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors"
                    data-testid={`session-item-${session.session_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm font-medium">
                        Session {session.session_number ?? '—'}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        session.status === 'active' ? 'bg-emerald-500/15 text-emerald-400'
                        : session.status === 'completed' ? 'bg-blue-500/15 text-blue-400'
                        : session.status === 'abandoned' ? 'bg-slate-600/30 text-slate-400'
                        : 'bg-slate-700/30 text-slate-500'
                      }`}>{session.status}</span>
                    </div>
                    {session.status === 'active' && session.step_label && (
                      <p className="text-slate-400 text-xs mt-0.5">
                        paused at {session.step_label}
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-0.5">
                      {session.created_at
                        ? new Date(session.created_at).toLocaleString(undefined, {
                            year: 'numeric', month: 'numeric', day: 'numeric',
                            hour: 'numeric', minute: '2-digit', hour12: true
                          })
                        : ''}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const OfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scraping, setScraping] = useState(false);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const response = await axios.get(`${API}/offers/${offerId}`);
        setOffer(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load offer");
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [offerId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !offer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error || "Offer not found"}</p>
          <button
            onClick={() => navigate("/pipeline")}
            className="text-emerald-400 hover:text-emerald-300"
          >
            Back to Pipeline
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate score color based on v2 weights
  const getScoreColor = (score, max) => {
    const pct = (score / max) * 100;
    if (pct >= 70) return "text-emerald-400";
    if (pct >= 40) return "text-amber-400";
    return "text-slate-400";
  };

  const getTotalScoreColor = (score) => {
    if (score >= 70) return "text-emerald-400";
    if (score >= 50) return "text-amber-400";
    if (score >= 30) return "text-purple-400";
    return "text-red-400";
  };

  // v2 Score criteria breakdown (40/30/20/5/5 weights)
  const scoreCriteria = [
    { 
      name: "Retail Price", 
      score: offer.retail_price_score || 0, 
      max: 40,
      description: offer.retail_price ? `$${offer.retail_price.toFixed(0)}` : (offer.retail_price_unconfirmed ? 'Unconfirmed' : 'Unknown'),
      detail: offer.retail_price_unconfirmed ? "AliExpress price is wholesale (COGS), not retail" : 
              offer.retail_price >= 150 ? "High retail (>=$150)" : 
              offer.retail_price >= 80 ? "Good retail (>=$80)" : "Low retail (<$80)"
    },
    { 
      name: "Trend Velocity", 
      score: offer.trend_velocity_score || 0, 
      max: 30,
      description: offer.google_trends_velocity ? `${offer.google_trends_velocity > 0 ? '+' : ''}${offer.google_trends_velocity.toFixed(0)}%` : 'Unknown',
      detail: offer.google_trends_velocity > 100 ? "Strong uptrend (>100%)" : 
              offer.google_trends_velocity > 0 ? "Growing" : 
              offer.google_trends_velocity < 0 ? "Declining" : "Stable"
    },
    { 
      name: "Social Saturation", 
      score: offer.social_saturation_score_component || (offer.social_validation_completed ? 10 : 0), 
      max: 20,
      description: offer.social_validation_completed ? 
        `TikTok: ${offer.tiktok_ad_count || 0}, Meta: ${offer.meta_ad_count || 0}` : 
        'Not validated',
      detail: offer.social_validation_completed ? 
        (offer.social_saturation_score <= 30 ? "Low saturation (good)" : 
         offer.social_saturation_score <= 60 ? "Moderate saturation" : "High saturation") :
        "Pending social validation"
    },
    { 
      name: "Supply Accessibility", 
      score: offer.supply_accessibility_score || 2.5, 
      max: 5,
      description: offer.aliexpress_rank ? `Rank #${offer.aliexpress_rank}` : (offer.cogs_estimate ? `~$${offer.cogs_estimate.toFixed(0)} COGS` : 'Unknown'),
      detail: offer.aliexpress_rank && offer.aliexpress_rank <= 20 ? "Easy to source" : "Moderate sourcing"
    },
    { 
      name: "Angle Availability", 
      score: offer.angle_availability_score || 2.5, 
      max: 5,
      description: "Creative angle variety",
      detail: (offer.tiktok_ad_count || 0) + (offer.meta_ad_count || 0) < 30 ? "Fresh angles available" : "Limited angles"
    },
  ];

  // Momentum indicator
  const getMomentumIndicator = () => {
    const delta = offer.score_delta || 0;
    if (delta > 5) return { icon: "↑↑", color: "text-emerald-400", label: "Strong Momentum" };
    if (delta > 0) return { icon: "↑", color: "text-emerald-400", label: "Gaining" };
    if (delta < -5) return { icon: "↓↓", color: "text-red-400", label: "Declining Fast" };
    if (delta < 0) return { icon: "↓", color: "text-red-400", label: "Declining" };
    return { icon: "→", color: "text-slate-400", label: "Stable" };
  };

  const momentum = getMomentumIndicator();

  const handleScrapeOffer = async () => {
    setScraping(true);
    try {
      const res = await axios.post(`${API}/offers/${offer.offer_id}/scrape`);
      alert(`Scraped: ${res.data.product_name || 'Product info'} — ${res.data.retail_price ? '$' + res.data.retail_price : 'No price found'}`);
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.detail || 'Scrape failed');
    } finally {
      setScraping(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="offer-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white mb-2 flex items-center gap-1"
              data-testid="back-button"
            >
              ← Back
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white" data-testid="offer-name">{offer.name}</h1>
              {offer.is_deleted && (
                <span className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300" data-testid="offer-archived-badge">
                  Archived
                </span>
              )}
            </div>
            <p className="text-slate-400 mt-1">
              {offer.offer_code} • {offer.source_platform?.toUpperCase() || offer.channel}
            </p>
          </div>
          <div className="flex items-start gap-4">
            <CreativeEngineDropdown offerId={offer.offer_id} />
            <div className="text-right">
              <div className={`text-4xl font-bold ${getTotalScoreColor(offer.current_score || 0)}`} data-testid="total-score">
                {(offer.current_score || 0).toFixed(0)}
              </div>
              <div className={`text-sm ${momentum.color} flex items-center justify-end gap-1`}>
                <span>{momentum.icon}</span>
                <span>{momentum.label}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Disqualification Banner */}
        {offer.disqualified && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 font-medium">Disqualified</p>
            <p className="text-red-300 text-sm mt-1">{offer.disqualification_reason}</p>
          </div>
        )}

        {/* Product Description & Link */}
        {(offer.product_description || offer.product_url || (offer.source_urls && offer.source_urls.length > 0)) && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {offer.vertical && (
                  <span className="inline-block px-2 py-0.5 bg-slate-700/50 text-slate-300 text-xs rounded mb-2" data-testid="offer-vertical-badge">
                    {offer.vertical}
                  </span>
                )}
                {offer.product_description && (
                  <p className="text-slate-300 text-sm" data-testid="product-description">
                    {offer.product_description}
                  </p>
                )}
                {offer.raw_title && offer.raw_title !== offer.name && (
                  <p className="text-slate-500 text-xs mt-2" data-testid="raw-title">
                    Original title: {offer.raw_title}
                  </p>
                )}
              </div>
              {offer.product_url && (
                <a 
                  href={offer.product_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors whitespace-nowrap"
                  data-testid="product-url-link"
                >
                  <span>View Source</span>
                  <span>↗</span>
                </a>
              )}
            </div>
            {/* Source URLs (Item 4) */}
            {offer.source_urls && offer.source_urls.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <p className="text-slate-500 text-xs mb-2">Source Links ({offer.source_urls.length})</p>
                <div className="space-y-1" data-testid="source-urls-list">
                  {offer.source_urls.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-emerald-400 hover:text-emerald-300 text-sm break-all"
                      data-testid={`source-url-${i}`}
                    >
                      {url}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Scoring Breakdown */}
          <div className="lg:col-span-2 space-y-6">
            {/* Score Breakdown Card */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Scoring Breakdown</h2>
              <div className="space-y-4">
                {scoreCriteria.map((criteria) => (
                  <div key={criteria.name} className="space-y-2" data-testid={`score-${criteria.name.toLowerCase()}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium">{criteria.name}</span>
                        <span className="text-slate-500 text-sm ml-2">{criteria.description}</span>
                      </div>
                      <span className={`font-bold ${getScoreColor(criteria.score, criteria.max)}`}>
                        {criteria.score}/{criteria.max}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          criteria.score / criteria.max >= 0.7 ? 'bg-emerald-500' : 
                          criteria.score / criteria.max >= 0.4 ? 'bg-amber-500' : 'bg-slate-600'
                        }`}
                        style={{ width: `${(criteria.score / criteria.max) * 100}%` }}
                      />
                    </div>
                    <p className="text-slate-500 text-xs">{criteria.detail}</p>
                  </div>
                ))}
              </div>
              
              {/* Total Score Summary */}
              <div className="mt-6 pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold">Total Score</span>
                  <span className={`text-2xl font-bold ${getTotalScoreColor(offer.current_score || 0)}`}>
                    {(offer.current_score || 0).toFixed(0)}/100
                  </span>
                </div>
              </div>
            </div>

            {/* Raw Scraped Data */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Raw Scraped Data</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Source Platform</p>
                  <p className="text-white">{offer.source_platform || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Search Term</p>
                  <p className="text-white">"{offer.search_term || "—"}"</p>
                </div>
                <div>
                  <p className="text-slate-500">Advertiser Page ID</p>
                  <p className="text-white font-mono text-xs">{offer.advertiser_page_id || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Source Ad ID</p>
                  <p className="text-white font-mono text-xs">{offer.source_ad_id || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">First Seen</p>
                  <p className="text-white">{offer.first_seen ? new Date(offer.first_seen).toLocaleDateString() : "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Updated</p>
                  <p className="text-white">{offer.updated_at ? new Date(offer.updated_at).toLocaleDateString() : "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Retail Price (Extracted)</p>
                  <p className="text-white">{offer.retail_price ? `$${offer.retail_price}` : "—"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Initial Score</p>
                  <p className="text-white">{offer.initial_score?.toFixed(1) || "—"}</p>
                </div>
              </div>
              
              {/* Source URL */}
              {(offer.source_url || (offer.source_urls && offer.source_urls.length > 0)) && (
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-slate-500 text-sm mb-2">Source URL(s)</p>
                  {offer.source_urls && offer.source_urls.length > 0 ? (
                    offer.source_urls.map((url, i) => (
                      <a 
                        key={i}
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block text-emerald-400 hover:text-emerald-300 text-sm break-all mb-1"
                        data-testid={`raw-source-url-${i}`}
                      >
                        {url}
                      </a>
                    ))
                  ) : offer.source_url ? (
                    <a 
                      href={offer.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 text-sm break-all"
                      data-testid="source-url"
                    >
                      {offer.source_url}
                    </a>
                  ) : null}
                </div>
              )}

              {/* Scrape Product Info Button */}
              {(offer.source_url || offer.product_url || (offer.source_urls && offer.source_urls.length > 0)) && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <button
                    onClick={handleScrapeOffer}
                    disabled={scraping}
                    className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 text-xs flex items-center gap-1.5 transition-colors"
                    data-testid="scrape-offer-btn"
                  >
                    {scraping ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-slate-400" />
                        Scraping...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Scrape Product Info
                      </>
                    )}
                  </button>
                  {offer.scrape_status === 'completed' && (
                    <p className="text-emerald-400 text-xs mt-1">Product info scraped</p>
                  )}
                  {offer.scrape_status === 'failed' && (
                    <p className="text-red-400 text-xs mt-1">Scrape failed: {offer.scrape_error}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Status & Actions */}
          <div className="space-y-6">
            {/* Advertiser Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Advertiser</h2>
              <p className="text-xl text-white" data-testid="advertiser-name">{offer.advertiser_name || "Unknown"}</p>
              {offer.advertiser_page_id && (
                <a 
                  href={`https://www.facebook.com/ads/library/?view_all_page_id=${offer.advertiser_page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 text-sm mt-2 inline-block"
                >
                  View all ads from this advertiser →
                </a>
              )}
            </div>

            {/* Retail Price Card - PROMINENT */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Retail Price</h2>
              <div className="space-y-3">
                <div>
                  <div className="text-3xl font-bold text-white" data-testid="retail-price">
                    {offer.retail_price ? `$${offer.retail_price.toFixed(2)}` : "—"}
                  </div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
                      offer.payout_status === 'confirmed' 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : offer.payout_status === 'estimated'
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-slate-700/50 text-slate-400 border border-slate-600'
                    }`} data-testid="price-confidence">
                      <span className={`w-2 h-2 rounded-full ${
                        offer.payout_status === 'confirmed' ? 'bg-emerald-400' :
                        offer.payout_status === 'estimated' ? 'bg-amber-400' :
                        'bg-slate-400'
                      }`}></span>
                      {offer.payout_status === 'confirmed' ? 'Confirmed' :
                       offer.payout_status === 'estimated' ? 'Estimated' : 'Unknown'}
                    </span>
                  </div>
                </div>
                <p className="text-slate-500 text-xs">
                  {offer.payout_status === 'confirmed' 
                    ? 'Price extracted from landing page' 
                    : offer.payout_status === 'estimated'
                    ? 'Price estimated from available data'
                    : 'Scraper could not detect price from landing page'}
                </p>
                {offer.retail_price && offer.retail_price >= 80 && (
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <span>✓</span>
                    <span>Passes $80 minimum gate</span>
                  </div>
                )}
              </div>
            </div>

            {/* Status Card */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Status</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-slate-500 text-sm">Offer Status</p>
                  <StatusBadge status={offer.status} />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Payout Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                    offer.payout_status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                    offer.payout_status === 'estimated' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-700 text-slate-400'
                  }`} data-testid="payout-status">
                    {offer.payout_status || 'unknown'}
                  </span>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Ad Running Days</p>
                  <p className="text-white text-lg" data-testid="running-days">{offer.ad_running_days || 0} days</p>
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Advertiser Count</p>
                  <p className="text-white text-lg" data-testid="advertiser-count">{offer.advertiser_count || 1} advertisers</p>
                </div>
              </div>
            </div>

            {/* Payout Card */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Payout</h2>
              <div className="text-3xl font-bold text-emerald-400" data-testid="payout-amount">
                ${offer.payout || 0}
              </div>
              <p className="text-slate-400">{offer.payout_type || "CPA"}</p>
              {offer.payout_confirmed_at && (
                <p className="text-slate-500 text-xs mt-2">
                  Confirmed {new Date(offer.payout_confirmed_at).toLocaleDateString()}
                  {offer.payout_confirmed_by && ` by ${offer.payout_confirmed_by}`}
                </p>
              )}
            </div>

            {/* Momentum Card */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Momentum</h2>
              <div className={`text-3xl font-bold ${momentum.color}`} data-testid="momentum-indicator">
                {momentum.icon} {(offer.score_delta || 0).toFixed(1)}
              </div>
              <p className={`${momentum.color}`}>{momentum.label}</p>
              <p className="text-slate-500 text-xs mt-2">Score change since last update</p>
            </div>

            {/* Notes */}
            {offer.notes && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4">Notes</h2>
                <p className="text-slate-300 text-sm">{offer.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ============================================================================
// OFFERS PAGE
// ============================================================================

const OffersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [incompleteOffers, setIncompleteOffers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("complete");
  const [verticalFilter, setVerticalFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [scoreThreshold, setScoreThreshold] = useState(0);
  // Soft-delete: founder-only "Show archived" view (opts in to include_deleted).
  const [showArchived, setShowArchived] = useState(false);

  // Single source of truth for loading the two offer lists, honoring the
  // archived toggle. Reused by hide/restore so the list stays in sync.
  const refreshOffers = useCallback(async () => {
    const inc = showArchived ? "&include_deleted=true" : "";
    const [completeRes, incRes] = await Promise.all([
      axios.get(`${API}/offers?incomplete=false${inc}`),
      axios.get(`${API}/offers?incomplete=true${inc}`),
    ]);
    setOffers(completeRes.data);
    setIncompleteOffers(incRes.data);
  }, [showArchived]);

  useEffect(() => { refreshOffers().catch((e) => console.error("Failed to load offers:", e)); }, [refreshOffers]);

  const handleHideOffer = async (offerId) => {
    if (!window.confirm("Hide this offer?")) return;
    try {
      await axios.post(`${API}/offers/${offerId}/delete`);
      await refreshOffers();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to hide offer");
    }
  };

  const handleRestoreOffer = async (offerId) => {
    try {
      await axios.post(`${API}/offers/${offerId}/restore`);
      await refreshOffers();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to restore offer");
    }
  };

  // Read vertical query param from URL (for seasonal alert navigation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vFilter = params.get("vertical");
    if (vFilter) setVerticalFilter(vFilter);
  }, []);
  const [addForm, setAddForm] = useState({
    offer_name: "",
    offer_link: "",
    network: "",
    vertical: "",
    sub_category: "",
    commission: "",
    run_times: "247",
    daily_cap: "",
    channel: "META",
    rules_of_engagement: "",
    business_unit: "AFFILIATE",
    // Required by POST /offers/manual since SETTINGS_OFFERS Phase A. Kept here
    // so this page keeps working until it is switched off; the v2 Settings →
    // Offers form is the replacement.
    tracking_platform: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [operatorsRes, buRes, netRes, vertRes] = await Promise.all([
          user.role === "FOUNDER" ? axios.get(`${API}/operators`) : Promise.resolve({ data: [] }),
          axios.get(`${API}/business-units`),
          axios.get(`${API}/networks`),
          axios.get(`${API}/offers/verticals`),
        ]);
        // Offer lists are loaded by refreshOffers() (archived-toggle aware).
        setOperators(operatorsRes.data.filter((op) => op.status === "active"));
        setBusinessUnits(buRes.data);
        setNetworks(netRes.data);
        setVerticals(vertRes.data.verticals || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleAssign = async (offerId, operatorId) => {
    try {
      await axios.patch(`${API}/offers/${offerId}/assign?operator_id=${operatorId}`);
      setOffers((prev) =>
        prev.map((o) => (o.offer_id === offerId ? { ...o, operator_id: operatorId } : o))
      );
      setShowAssignModal(null);
    } catch (error) {
      console.error("Failed to assign offer:", error);
    }
  };

  const handleAddOffer = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...addForm,
        commission: parseFloat(addForm.commission),
        daily_cap: addForm.daily_cap ? parseInt(addForm.daily_cap) : null,
      };
      const response = await axios.post(`${API}/offers/manual`, payload);
      // Refresh offers lists (archived-toggle aware)
      await refreshOffers();
      setShowAddModal(false);
      setAddForm({
        offer_name: "", offer_link: "", network: "", vertical: "",
        sub_category: "", commission: "", run_times: "247",
        daily_cap: "", channel: "META", rules_of_engagement: "",
        business_unit: "AFFILIATE", tracking_platform: "",
      });
      // Navigate to the new offer
      if (response.data.offer_id) {
        navigate(`/offers/${response.data.offer_id}`);
      }
    } catch (error) {
      console.error("Failed to create offer:", error);
      alert(error.response?.data?.detail || "Failed to create offer");
    } finally {
      setSubmitting(false);
    }
  };

  // Apply filters to the active list
  const _getSourceLabel = (src) => {
    const labels = {
      'aliexpress_trending_agent': 'AliExpress',
      'amazon_bsr_agent': 'Amazon',
      'google_trends_agent': 'Trends',
      'reddit_signal_agent': 'Reddit',
      'manual_upload': 'Manual',
      'telegram_requested': 'Requested',
    };
    return labels[src] || src?.split('_')[0] || 'Unknown';
  };
  const currentOffers = activeTab === "complete" ? offers : incompleteOffers;
  const filteredOffers = currentOffers.filter(o => {
    if (verticalFilter !== "all" && o.vertical !== verticalFilter) return false;
    if (sourceFilter !== "all" && o.discovery_source !== sourceFilter) return false;
    if (scoreThreshold > 0 && (o.current_score || 0) < scoreThreshold) return false;
    return true;
  });

  // Source counts
  const sourceCounts = {};
  currentOffers.forEach(o => {
    const src = o.discovery_source || "unknown";
    sourceCounts[src] = (sourceCounts[src] || 0) + 1;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white" data-testid="offers-title">
              Offers
            </h2>
            <p className="text-slate-400 mt-1">Manage marketing offers</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Soft-delete: founder-only archived view + restore safety net */}
            {user.role === "FOUNDER" && (
              <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer select-none" data-testid="show-archived-toggle">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-4 w-4 accent-emerald-500 cursor-pointer"
                />
                Show archived
              </label>
            )}
            {["FOUNDER", "MEDIA_BUYER"].includes(user.role) && (
              <button
                onClick={() => setShowAddModal(true)}
                data-testid="add-offer-btn"
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
              >
                Add Offer
              </button>
            )}
          </div>
        </div>

        {/* Tabs: Complete / Incomplete */}
        <div className="flex items-center gap-6 border-b border-slate-800">
          <button
            onClick={() => setActiveTab("complete")}
            data-testid="tab-complete"
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "complete"
                ? "text-emerald-400 border-b-2 border-emerald-400"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            Complete ({offers.length})
          </button>
          <button
            onClick={() => setActiveTab("incomplete")}
            data-testid="tab-incomplete"
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "incomplete"
                ? "text-purple-400 border-b-2 border-purple-400"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            Incomplete ({incompleteOffers.length})
          </button>
        </div>

        {/* Filters: Vertical + Source + Score */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Vertical:</span>
            <select
              value={verticalFilter}
              onChange={(e) => setVerticalFilter(e.target.value)}
              data-testid="vertical-filter-dropdown"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="all">All Verticals</option>
              {verticals.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Source:</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              data-testid="source-filter-dropdown"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="all">All Sources</option>
              {Object.entries(sourceCounts).map(([src, count]) => (
                <option key={src} value={src}>{_getSourceLabel(src)} ({count})</option>
              ))}
            </select>
          </div>
          <ScoreFilter onFilterChange={setScoreThreshold} type="discovery" />
          {(verticalFilter !== "all" || sourceFilter !== "all") && (
            <button
              onClick={() => { setVerticalFilter("all"); setSourceFilter("all"); }}
              className="text-xs text-slate-500 hover:text-white transition-colors"
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
          <span className="text-slate-500 text-sm ml-auto">
            Showing {filteredOffers.length} of {currentOffers.length}
          </span>
          <a
            href={`${API}/export/offers${verticalFilter !== 'all' ? '?vertical=' + verticalFilter : ''}${sourceFilter !== 'all' ? (verticalFilter !== 'all' ? '&' : '?') + 'source=' + sourceFilter : ''}`}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
            data-testid="export-offers-csv"
          >
            Export CSV
          </a>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Offer</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Vertical</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Source</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Score</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Price</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Operator</th>
                  {["FOUNDER", "MEDIA_BUYER"].includes(user.role) && (
                    <th className="px-6 py-4 text-right text-sm font-medium text-slate-400">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {[...filteredOffers].sort((a, b) => (b.current_score || 0) - (a.current_score || 0)).map((offer) => {
                  const assignedOp = operators.find((op) => op.operator_id === offer.operator_id);
                  // Discovery source badge
                  const getSourceBadge = (source) => {
                    const badges = {
                      'aliexpress_trending_agent': { label: 'AliExpress', bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
                      'amazon_bsr_agent': { label: 'Amazon', bg: 'bg-amber-500/20', text: 'text-amber-400' },
                      'google_trends_agent': { label: 'Trends', bg: 'bg-blue-500/20', text: 'text-blue-400' },
                      'reddit_signal_agent': { label: 'Reddit', bg: 'bg-red-500/20', text: 'text-red-400' },
                      'manual_upload': { label: 'Manual', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
                      'telegram_requested': { label: 'Requested', bg: 'bg-violet-500/20', text: 'text-violet-400' },
                    };
                    return badges[source] || { label: source?.split('_')[0] || 'Manual', bg: 'bg-slate-500/20', text: 'text-slate-400' };
                  };
                  const sourceBadge = getSourceBadge(offer.discovery_source);
                  return (
                    <tr 
                      key={offer.offer_id} 
                      className="border-b border-slate-800/50 cursor-pointer transition-all duration-200"
                      onClick={() => navigate(`/offers/${offer.offer_id}`)}
                      data-testid={`offer-row-${offer.offer_id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="text-white font-medium">{offer.name}</p>
                        {offer.offer_code && (
                          <div className="flex items-center gap-1.5" title="Paste this offer code as the first segment of your Meta + RedTrack campaign names">
                            <code className="text-slate-400 text-sm font-mono">{offer.offer_code}</code>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(offer.offer_code); }}
                              data-testid={`copy-offer-code-${offer.offer_id}`}
                              className="text-slate-500 hover:text-emerald-400 text-xs px-1"
                              title="Copy offer code"
                            >⧉ Copy</button>
                          </div>
                        )}
                        {/* Additive: product_name, funnel_url, dates + edit/delete (same component as pipeline) */}
                        <OfferCardActions offer={offer} onChanged={refreshOffers} />
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">{offer.vertical || '—'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs ${sourceBadge.bg} ${sourceBadge.text}`}>
                          {sourceBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-bold ${
                          (offer.current_score || 0) >= 70 ? 'text-emerald-400' : 
                          (offer.current_score || 0) >= 50 ? 'text-amber-400' : 'text-slate-400'
                        }`}>
                          {offer.current_score?.toFixed(0) || '—'}
                        </span>
                        {offer.retail_price_unconfirmed && (
                          <span className="ml-2 text-purple-400 text-xs" title="Retail price unconfirmed">!</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {offer.retail_price ? (
                          <span className="text-emerald-400">${offer.retail_price.toFixed(0)}</span>
                        ) : offer.cogs_estimate ? (
                          <span className="text-slate-400">~${offer.cogs_estimate.toFixed(0)} COGS</span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={offer.status} />
                        {offer.is_deleted && (
                          <span className="ml-2 px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-400" data-testid={`archived-badge-${offer.offer_id}`}>
                            Archived
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {assignedOp?.operator_code || (
                          <span className="text-slate-500">Unassigned</span>
                        )}
                      </td>
                      {["FOUNDER", "MEDIA_BUYER"].includes(user.role) && (
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          {user.role === "FOUNDER" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAssignModal(offer);
                              }}
                              className="text-emerald-400 hover:text-emerald-300 text-sm"
                            >
                              Assign
                            </button>
                          )}
                          {offer.is_deleted ? (
                            // Restore is founder-only (the safety net).
                            user.role === "FOUNDER" && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRestoreOffer(offer.offer_id); }}
                                data-testid={`restore-offer-${offer.offer_id}`}
                                className="ml-3 text-amber-400 hover:text-amber-300 text-sm"
                              >
                                Restore
                              </button>
                            )
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleHideOffer(offer.offer_id); }}
                              data-testid={`hide-offer-${offer.offer_id}`}
                              className="ml-3 text-slate-400 hover:text-rose-300 text-sm"
                            >
                              Hide
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              Assign Offer: {showAssignModal.name}
            </h3>
            <div className="space-y-3">
              {operators.map((op) => (
                <button
                  key={op.operator_id}
                  onClick={() => handleAssign(showAssignModal.offer_id, op.operator_id)}
                  className="w-full p-4 bg-slate-800 hover:bg-slate-700 rounded-lg text-left transition-colors"
                >
                  <p className="text-white font-medium">{op.name}</p>
                  <p className="text-slate-400 text-sm">{op.operator_code}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowAssignModal(null)}
              className="mt-4 w-full py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Offer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-xl mx-4" data-testid="add-offer-modal">
            <h3 className="text-lg font-semibold text-white mb-1">Add Offer</h3>
            <p className="text-slate-500 text-sm mb-3">Manual upload — triggers Ghost Recon spy session + Creative Engine pipeline</p>
            {/* Offer-code helper: the code is auto-generated on save; buyers paste it into campaign names. */}
            <div className="mb-4 rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2" data-testid="offer-code-helper">
              <p className="text-xs text-slate-400">
                A canonical <span className="text-emerald-400 font-mono">offer code</span> is auto-generated on save. Paste it as the <b>first</b> segment of your Meta + RedTrack campaign/ad names (e.g. <span className="font-mono text-slate-300">HOMEAP001-NN-META-VE</span>) so Cipher can link spend to revenue.
              </p>
            </div>

            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
              {/* Business Unit */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit *</label>
                <div className="flex gap-2">
                  {businessUnits.map(bu => (
                    <button
                      key={bu.business_unit_id}
                      onClick={() => setAddForm({ ...addForm, business_unit: bu.name })}
                      data-testid={`bu-select-${bu.name.toLowerCase()}`}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        addForm.business_unit === bu.name
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                    >
                      {bu.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Offer Name */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Offer Name *</label>
                <input
                  type="text"
                  value={addForm.offer_name}
                  onChange={(e) => setAddForm({ ...addForm, offer_name: e.target.value })}
                  placeholder="Auto Insurance"
                  data-testid="add-offer-name"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>

              {/* Offer Link */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Offer Link *</label>
                <input
                  type="url"
                  value={addForm.offer_link}
                  onChange={(e) => setAddForm({ ...addForm, offer_link: e.target.value })}
                  placeholder="https://network.com/offer/12345"
                  data-testid="add-offer-link"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Network */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Network *</label>
                  <input
                    type="text"
                    value={addForm.network}
                    onChange={(e) => setAddForm({ ...addForm, network: e.target.value })}
                    placeholder="BlueModo Media"
                    list="network-options"
                    data-testid="add-offer-network"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  />
                  <datalist id="network-options">
                    {networks.map((n) => (
                      <option key={n.network_id} value={n.name} />
                    ))}
                  </datalist>
                </div>

                {/* Vertical */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Vertical *</label>
                  <select
                    value={addForm.vertical}
                    onChange={(e) => setAddForm({ ...addForm, vertical: e.target.value })}
                    data-testid="add-offer-vertical"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="LEADGEN">LEADGEN</option>
                    <option value="ECOM">ECOM</option>
                  </select>
                </div>
              </div>

              {/* Tracking Platform — required on the offer, every time. There is
                  no networks surface and it is not stored on the network. */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Tracking Platform *</label>
                <select
                  value={addForm.tracking_platform}
                  onChange={(e) => setAddForm({ ...addForm, tracking_platform: e.target.value })}
                  data-testid="add-offer-tracking-platform"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select...</option>
                  <option value="CAKE">Cake</option>
                  <option value="EVERFLOW">Everflow</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Sub-Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Sub-Category *</label>
                  <input
                    type="text"
                    value={addForm.sub_category}
                    onChange={(e) => setAddForm({ ...addForm, sub_category: e.target.value })}
                    placeholder="Insurance"
                    list="subcategory-options"
                    data-testid="add-offer-subcategory"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  />
                  <datalist id="subcategory-options">
                    <option value="Insurance" />
                    <option value="Financial" />
                    <option value="Health" />
                    <option value="Solar" />
                    <option value="Home Services" />
                    <option value="Education" />
                    <option value="Legal" />
                    <option value="Beauty" />
                    <option value="Supplements" />
                    <option value="Weight Loss" />
                  </datalist>
                </div>

                {/* Commission */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Commission ($) *</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={addForm.commission}
                    onChange={(e) => setAddForm({ ...addForm, commission: e.target.value })}
                    placeholder="0"
                    data-testid="add-offer-commission"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Run Times */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Run Times *</label>
                  <select
                    value={addForm.run_times}
                    onChange={(e) => setAddForm({ ...addForm, run_times: e.target.value })}
                    data-testid="add-offer-runtimes"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="247">24/7</option>
                    <option value="9to5">9 to 5</option>
                    <option value="8to8">8 to 8</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {/* Daily Cap */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Daily Cap</label>
                  <input
                    type="number"
                    value={addForm.daily_cap}
                    onChange={(e) => setAddForm({ ...addForm, daily_cap: e.target.value })}
                    placeholder="Uncapped"
                    data-testid="add-offer-dailycap"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  />
                </div>

                {/* Channel */}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Channel *</label>
                  <select
                    value={addForm.channel}
                    onChange={(e) => setAddForm({ ...addForm, channel: e.target.value })}
                    data-testid="add-offer-channel"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="META">META</option>
                    <option value="NATIVE">NATIVE</option>
                    <option value="TIKTOK">TIKTOK</option>
                    <option value="GOOGLE">GOOGLE</option>
                    <option value="YOUTUBE">YOUTUBE</option>
                  </select>
                </div>
              </div>

              {/* Rules of Engagement */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Rules of Engagement</label>
                <textarea
                  value={addForm.rules_of_engagement}
                  onChange={(e) => setAddForm({ ...addForm, rules_of_engagement: e.target.value })}
                  rows={4}
                  placeholder="Approved states, special restrictions, network notes, compliance requirements..."
                  data-testid="add-offer-rules"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-2 text-slate-400 hover:text-white transition-colors"
                data-testid="add-offer-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleAddOffer}
                disabled={
                  submitting ||
                  !addForm.offer_name ||
                  !addForm.offer_link ||
                  !addForm.network ||
                  !addForm.vertical ||
                  !addForm.tracking_platform ||
                  !addForm.sub_category ||
                  !addForm.commission
                }
                data-testid="add-offer-submit"
                className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
              >
                {submitting ? "Creating..." : "Add Offer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ============================================================================
// CAMPAIGNS PAGE
// ============================================================================

const CampaignsPage = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailTab, setDetailTab] = useState("creative");
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [offers, setOffers] = useState([]);
  // Option B: campaigns whose parent offer was soft-deleted get an "archived
  // offer" label. The picker `offers` (default-excluded) correctly omits hidden
  // offers; this separate set carries the deleted ids just for the label.
  const [deletedOfferIds, setDeletedOfferIds] = useState(() => new Set());

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const [campRes, perfRes, offersRes, allOffersRes] = await Promise.all([
          axios.get(`${API}/campaigns`),
          axios.get(`${API}/performance/campaigns`).catch(() => ({ data: [] })),
          axios.get(`${API}/offers?incomplete=false`).catch(() => ({ data: [] })),
          axios.get(`${API}/offers?include_deleted=true`).catch(() => ({ data: [] })),
        ]);
        setDeletedOfferIds(new Set(
          (Array.isArray(allOffersRes.data) ? allOffersRes.data : [])
            .filter((o) => o.is_deleted)
            .map((o) => o.offer_id)
        ));
        const formal = campRes.data || [];
        const perfCamps = (perfRes.data || []).map(p => ({
          campaign_id: p.campaign_id,
          campaign_name: p.campaign_name,
          channel: p.channel || 'N/A',
          status: 'active',
          business_unit_id: p.business_unit_id || '',
          offer_id: p.offer_id || '',
          operator_id: p.operator_id || '',
        }));
        const formalIds = new Set(formal.map(c => c.campaign_id));
        setCampaigns([...formal, ...perfCamps.filter(p => !formalIds.has(p.campaign_id))]);
        setOffers(Array.isArray(offersRes.data) ? offersRes.data : []);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);


  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };


  const openCampaignDetail = async (campaign) => {
    setSelectedCampaign(campaign);
    setDetailTab("creative");
    setCampaignDetail(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.campaign_id}/detail`);
      setCampaignDetail(res.data);
    } catch (e) {
      console.debug("Campaign detail fetch failed:", e);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {/* WARNING for future App.js cleanup: this Campaigns screen is ABH's live
          v1 campaigns page, implemented inline here rather than in its own
          component file like every other ABH screen. Per D-07-REVERSAL
          (docs/DECISION_LOG.md, 18 Aug 2026), ABH is staying on v1
          indefinitely — it is not migrating to v2 — so this inline
          implementation is not a temporary migration artifact. Any
          restructuring pass through App.js touches this screen's actual
          implementation, not just a route registration. Read the CampaignsPage
          function boundaries before editing. */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white" data-testid="campaigns-title">
              Campaigns
            </h2>
            <p className="text-slate-400 mt-1">Manage marketing campaigns</p>
          </div>
        </div>

        <WinnerRotatorPanel defaultDays={14} />

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-12 text-center">
            <p className="text-slate-400">No campaigns yet. Create your first campaign.</p>
          </div>
        ) : (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Campaign</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Channel</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Phase</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-slate-400">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.campaign_id}
                    className={`hover:bg-slate-800/30 cursor-pointer ${selectedCampaign?.campaign_id === campaign.campaign_id ? 'bg-emerald-500/5 border-l-2 border-emerald-500' : ''}`}
                    onClick={() => openCampaignDetail(campaign)}
                    data-testid={`campaign-row-${campaign.campaign_id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium font-mono text-sm">
                          {campaign.campaign_name}
                        </p>
                        {campaign.offer_id && deletedOfferIds.has(campaign.offer_id) && (
                          <span
                            className="flex-shrink-0 px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-400"
                            title="This campaign's parent offer was archived (hidden from the Offers list). The campaign is unaffected and still running."
                            data-testid={`campaign-archived-offer-${campaign.campaign_id}`}
                          >
                            archived offer
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(campaign.campaign_name, campaign.campaign_id); }}
                          className={`flex-shrink-0 p-1 rounded transition-colors ${
                            copiedId === campaign.campaign_id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'
                          }`}
                          title="Copy campaign name"
                          data-testid={`copy-name-btn-${campaign.campaign_id}`}
                        >
                          {copiedId === campaign.campaign_id ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{campaign.channel}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-sm">
                        {campaign.phase}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={campaign.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Campaign Detail Panel */}
        {selectedCampaign && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid="campaign-detail-panel">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold font-mono">{selectedCampaign.campaign_name}</h3>
                    <button
                      onClick={() => copyToClipboard(selectedCampaign.campaign_name, 'detail-copy')}
                      className={`p-1 rounded transition-colors ${
                        copiedId === 'detail-copy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-400 hover:text-white'
                      }`}
                      data-testid="detail-copy-name-btn"
                    >
                      {copiedId === 'detail-copy' ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                  </div>
                  <p className="text-slate-400 text-sm">{selectedCampaign.channel} — {selectedCampaign.status}</p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); }}
                className="text-slate-500 hover:text-white transition-colors text-xl"
                data-testid="close-detail-btn"
              >
                &times;
              </button>
            </div>
            <div className="flex items-center gap-6 px-6 border-b border-slate-800">
              <button
                onClick={() => setDetailTab("creative")}
                data-testid="tab-creative"
                className={`pb-3 pt-3 text-sm font-medium transition-colors ${
                  detailTab === "creative" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Creative Detail
              </button>
              <button
                onClick={() => setDetailTab("performance")}
                data-testid="tab-performance"
                className={`pb-3 pt-3 text-sm font-medium transition-colors ${
                  detailTab === "performance" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Performance
              </button>
              <button
                onClick={() => setDetailTab("audience")}
                data-testid="tab-audience"
                className={`pb-3 pt-3 text-sm font-medium transition-colors ${
                  detailTab === "audience" ? "text-emerald-400 border-b-2 border-emerald-400" : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Audience
              </button>
            </div>
            <div className="p-6">
              {detailTab === "creative" && (
                detailLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
                  </div>
                ) : campaignDetail ? (
                  <div className="space-y-6" data-testid="creative-detail-content">
                    {/* Offer Info */}
                    {campaignDetail.offer && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <span className="text-slate-500 text-xs block mb-1">Offer</span>
                          <span className="text-white text-sm font-medium">{campaignDetail.offer.name}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs block mb-1">Creative Type</span>
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                            campaignDetail.production?.creative_type === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                            campaignDetail.production?.creative_type === 'VE' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-pink-500/20 text-pink-400'
                          }`}>{campaignDetail.production?.creative_type || campaignDetail.creative?.creative_type || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs block mb-1">Status</span>
                          <span className="text-white text-sm">{campaignDetail.production?.status || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs block mb-1">Channel</span>
                          <span className="text-white text-sm">{campaignDetail.offer.channel || selectedCampaign.channel}</span>
                        </div>
                      </div>
                    )}

                    {/* Copy Elements — Actual Text Content */}
                    {Object.keys(campaignDetail.copy_elements || {}).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3">Copy Elements</h4>
                        <div className="space-y-3">
                          {campaignDetail.copy_elements.hook && (
                            <div className="bg-slate-800/50 rounded-lg p-3" data-testid="copy-hook-content">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">
                                  {campaignDetail.copy_elements.hook.element_number}
                                </span>
                                <span className="text-slate-400 text-xs">Hook{campaignDetail.copy_elements.hook.hook_type ? ` — ${campaignDetail.copy_elements.hook.hook_type}` : ''}</span>
                              </div>
                              <p className="text-white text-sm">{campaignDetail.copy_elements.hook.content}</p>
                            </div>
                          )}
                          {campaignDetail.copy_elements.body && (
                            <div className="bg-slate-800/50 rounded-lg p-3" data-testid="copy-body-content">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">
                                  {campaignDetail.copy_elements.body.element_number}
                                </span>
                                <span className="text-slate-400 text-xs">Body</span>
                              </div>
                              <p className="text-white text-sm">{campaignDetail.copy_elements.body.content}</p>
                            </div>
                          )}
                          {campaignDetail.copy_elements.cta && (
                            <div className="bg-slate-800/50 rounded-lg p-3" data-testid="copy-cta-content">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">
                                  {campaignDetail.copy_elements.cta.element_number}
                                </span>
                                <span className="text-slate-400 text-xs">CTA</span>
                              </div>
                              <p className="text-white text-sm">{campaignDetail.copy_elements.cta.content}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Drive Links */}
                    {campaignDetail.production && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-3">Assets</h4>
                        <div className="flex flex-wrap gap-2">
                          {(campaignDetail.production.google_drive_url || campaignDetail.production.drive_web_link) && (
                            <a href={campaignDetail.production.google_drive_url || campaignDetail.production.drive_web_link}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-cyan-400 text-xs hover:bg-cyan-500/20 transition-colors"
                              data-testid="campaign-drive-link">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              {campaignDetail.production.multi_clip ? 'VEO Folder in Drive' : 'Video in Drive'}
                            </a>
                          )}
                          {campaignDetail.offer?.product_content_folder_url && (
                            <a href={campaignDetail.offer.product_content_folder_url}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 border border-slate-600/30 rounded-lg text-slate-300 text-xs hover:bg-slate-700 transition-colors"
                              data-testid="campaign-offer-drive-link">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              Offer Drive Folder
                            </a>
                          )}
                          {campaignDetail.production.trello_card_url && (
                            <a href={campaignDetail.production.trello_card_url}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400 text-xs hover:bg-blue-500/20 transition-colors"
                              data-testid="campaign-trello-link">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              Trello Card
                            </a>
                          )}
                        </div>
                        {/* Multi-clip individual links */}
                        {campaignDetail.production.multi_clip && campaignDetail.production.clip_drive_links?.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-slate-500 text-xs font-medium">Individual Clips</p>
                            {campaignDetail.production.clip_drive_links.map(clip => (
                              <div key={clip.clip} className="flex items-center gap-2 text-xs">
                                <span className="text-slate-400 font-mono">Clip {clip.clip}</span>
                                <span className="text-slate-500">{clip.filename}</span>
                                {clip.web_link && (
                                  <a href={clip.web_link} target="_blank" rel="noopener noreferrer"
                                    className="text-cyan-400 hover:text-cyan-300">Open</a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Script */}
                    {campaignDetail.script && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-2">Script</h4>
                        <div className="bg-slate-800/30 rounded-lg p-3 text-sm text-slate-300 max-h-48 overflow-y-auto" data-testid="campaign-script-content">
                          {campaignDetail.script.sections?.map((s, i) => (
                            <div key={i} className="mb-2">
                              <span className="text-emerald-400 text-xs font-medium">{s.section_label || `Section ${i+1}`}</span>
                              <p className="mt-0.5">{s.spoken_text || s.content || ''}</p>
                            </div>
                          )) || <p>{campaignDetail.script.full_script_text || 'No script text available'}</p>}
                        </div>
                      </div>
                    )}

                    {/* Veo Prompt */}
                    {campaignDetail.production?.veo_prompt && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-2">Veo Prompt</h4>
                        <pre className="bg-slate-800/30 rounded-lg p-3 text-xs text-slate-400 max-h-32 overflow-y-auto whitespace-pre-wrap font-mono" data-testid="campaign-veo-prompt">
                          {campaignDetail.production.veo_prompt}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No creative detail available for this campaign.</p>
                )
              )}
              {detailTab === "performance" && <CampaignPerformanceTab campaignId={selectedCampaign.campaign_id} />}
              {detailTab === "audience" && <AudienceSegmentsTab campaignId={selectedCampaign.campaign_id} />}
            </div>
          </div>
        )}
      </div>

      {/* The Create Campaign modal was removed 2026-09-02
          (CAMPAIGNS-REMOVAL-EXECUTE). Registration is gone; this screen stays
          because it is ABH's live campaigns page (D-07-REVERSAL) and it shows
          campaigns derived from actual spend, not from registration.
          See docs/audit/TOMBSTONE_CAMPAIGNS.md. */}
    </DashboardLayout>
  );
};

// ============================================================================
// OPERATORS PAGE (FOUNDER ONLY)
// ============================================================================

const OperatorsPage = () => {
  const { user } = useAuth();
  const [operators, setOperators] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedCoaching, setSelectedCoaching] = useState(null);
  const [invite, setInvite] = useState({
    email: "",
    operator_code: "",
    name: "",
    role: "OPERATOR",
    business_unit_ids: [],
  });
  // Inline per-operator role-change error (e.g. JC hard-block / last-founder 409).
  const [roleError, setRoleError] = useState({});      // { [operator_id]: message }
  const [roleSaving, setRoleSaving] = useState(null);   // operator_id currently saving

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [opsRes, busRes] = await Promise.all([
          axios.get(`${API}/operators`),
          axios.get(`${API}/business-units`),
        ]);
        setOperators(opsRes.data);
        setBusinessUnits(busRes.data);
        // Pre-check AFFILIATE by default — every operator needs at least
        // that one BU.
        const affiliate = (busRes.data || []).find(
          (b) => (b.name || "").toUpperCase() === "AFFILIATE"
        );
        if (affiliate) {
          setInvite((prev) => ({
            ...prev,
            business_unit_ids: prev.business_unit_ids.length
              ? prev.business_unit_ids
              : [affiliate.business_unit_id],
          }));
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fix 5 — inline error / success state inside the Invite modal. No more
  // browser alert() — and crucially no more "[object Object]" leak from
  // FastAPI/Pydantic validation arrays.
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteBUError, setInviteBUError] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  // Friendly extractor for FastAPI/axios error shapes. The detail field can be
  // a string OR a list of {loc, msg, type, ...} objects (Pydantic) which is
  // what used to render as "[object Object]" through alert().
  const formatBackendError = (err) => {
    const raw = err?.response?.data?.detail ?? err?.response?.data ?? err?.message ?? err;
    if (typeof raw === "string") return raw;
    if (Array.isArray(raw)) {
      return raw
        .map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d))))
        .join("; ");
    }
    if (raw && typeof raw === "object") {
      if (raw.msg) return raw.msg;
      if (raw.detail && typeof raw.detail === "string") return raw.detail;
      try { return JSON.stringify(raw); } catch { return "Unknown error"; }
    }
    return "Unknown error";
  };

  // Frontend validators (Fix 5 requirements 5 + 6).
  // Returns {topError, buError} so the Business Units field error can be
  // rendered inline under that section instead of dumped at the bottom.
  const validateInvite = () => {
    let topError = null;
    let buError = null;
    if (!invite.email?.includes("@")) topError = "Enter a valid email address.";
    else if (!invite.name?.trim()) topError = "Operator name is required.";
    else {
      const code = invite.operator_code || "";
      if (!code) topError = "Operator code is required.";
      else if (/\s/.test(code)) topError = "Operator code must contain no spaces.";
      else if (code !== code.toUpperCase()) topError = "Operator code must be ALL CAPS.";
    }
    if (!Array.isArray(invite.business_unit_ids) || invite.business_unit_ids.length === 0) {
      buError = "Select at least one business unit.";
    }
    return { topError, buError };
  };

  const handleInvite = async () => {
    setInviteError(null);
    setInviteBUError(null);
    setInviteSuccess(null);
    const { topError, buError } = validateInvite();
    if (topError || buError) {
      setInviteError(topError);
      setInviteBUError(buError);
      return;
    }
    setInviteSubmitting(true);
    try {
      const response = await axios.post(`${API}/operators/invite`, invite);
      setOperators([...operators, { ...invite, ...response.data, status: "pending" }]);
      setInviteSuccess(
        `Invite sent to ${invite.email}. They'll receive setup instructions.`
      );
      // Reset the form (re-pre-check AFFILIATE) but keep modal open briefly so
      // the user sees the confirmation. The modal closes itself after a moment.
      const affiliate = (businessUnits || []).find(
        (b) => (b.name || "").toUpperCase() === "AFFILIATE"
      );
      setInvite({
        email: "",
        operator_code: "",
        name: "",
        role: "OPERATOR",
        business_unit_ids: affiliate ? [affiliate.business_unit_id] : [],
      });
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(null);
      }, 2000);
    } catch (error) {
      console.error("Failed to invite:", error);
      setInviteError(formatBackendError(error));
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Founder-only role change. Surfaces the backend 409 (JC hard-block /
  // last-founder guard) and 422 (invalid role) inline next to the operator.
  const handleRoleChange = async (op, newRole) => {
    if (newRole === op.role) return;
    setRoleError((prev) => ({ ...prev, [op.operator_id]: null }));
    setRoleSaving(op.operator_id);
    try {
      await axios.patch(`${API}/operators/${op.operator_id}/role`, { role: newRole });
      setOperators((prev) =>
        prev.map((o) => (o.operator_id === op.operator_id ? { ...o, role: newRole } : o))
      );
    } catch (error) {
      console.error("Failed to change role:", error);
      setRoleError((prev) => ({ ...prev, [op.operator_id]: formatBackendError(error) }));
    } finally {
      setRoleSaving(null);
    }
  };

  if (user?.role !== "FOUNDER") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white" data-testid="operators-title">
              Operators
            </h2>
            <p className="text-slate-400 mt-1">Manage team members</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            data-testid="invite-operator-btn"
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
          >
            Invite Operator
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {operators.map((op) => (
              <div
                key={op.operator_id}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
              >
                <div className="flex items-center gap-4 mb-4">
                  {op.picture ? (
                    <img src={op.picture} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                      {op.operator_code?.[0]}
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">{op.name || op.operator_code}</p>
                    <p className="text-slate-500 text-sm">{op.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">{op.operator_code}</span>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`role-select-${op.operator_id}`}
                      value={op.role}
                      disabled={roleSaving === op.operator_id}
                      onChange={(e) => handleRoleChange(op, e.target.value)}
                      className="px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-200 focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                    >
                      <option value="OPERATOR">Operator</option>
                      <option value="MEDIA_BUYER">Media Buyer</option>
                      <option value="FOUNDER">Founder</option>
                    </select>
                    <button
                      data-testid={`coaching-btn-${op.operator_id}`}
                      onClick={() => setSelectedCoaching(selectedCoaching === op.operator_id ? null : op.operator_id)}
                      className="px-2 py-1 text-xs bg-amber-600/80 hover:bg-amber-500 text-white rounded transition-colors"
                    >
                      {selectedCoaching === op.operator_id ? "Close" : "Coaching"}
                    </button>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        op.status === "active"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : op.status === "pending"
                          ? "bg-amber-500/10 text-amber-400"
                          : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {op.status}
                    </span>
                  </div>
                </div>
                {roleError[op.operator_id] && (
                  <div data-testid={`role-error-${op.operator_id}`} className="mt-2 text-xs text-rose-300">
                    {roleError[op.operator_id]}
                  </div>
                )}
                {selectedCoaching === op.operator_id && (
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <OperatorCoachingProfile operatorId={op.operator_id} operatorName={op.name || op.operator_code} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Invite Operator</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Email</label>
                <input
                  type="email"
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Operator Code (e.g., MATT, STEVE)
                </label>
                <input
                  type="text"
                  value={invite.operator_code}
                  onChange={(e) =>
                    setInvite({ ...invite, operator_code: e.target.value.toUpperCase() })
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white uppercase focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={invite.name}
                  onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Role</label>
                <select
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  data-testid="invite-role-select"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="OPERATOR">Operator</option>
                  <option value="MEDIA_BUYER">Media Buyer</option>
                  <option value="FOUNDER">Founder</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Business Units
                </label>
                <div className="space-y-2" data-testid="invite-bu-list">
                  {businessUnits.map((bu) => {
                    const checked = invite.business_unit_ids.includes(bu.business_unit_id);
                    return (
                      <label
                        key={bu.business_unit_id}
                        htmlFor={`invite-bu-${bu.business_unit_id}`}
                        className="flex items-center gap-3 cursor-pointer select-none px-2 py-1 rounded hover:bg-slate-800/60"
                      >
                        <input
                          id={`invite-bu-${bu.business_unit_id}`}
                          type="checkbox"
                          data-testid={`invite-bu-checkbox-${bu.name}`}
                          checked={checked}
                          onChange={(e) => {
                            setInviteBUError(null);
                            if (e.target.checked) {
                              setInvite({
                                ...invite,
                                business_unit_ids: [...invite.business_unit_ids, bu.business_unit_id],
                              });
                            } else {
                              setInvite({
                                ...invite,
                                business_unit_ids: invite.business_unit_ids.filter(
                                  (id) => id !== bu.business_unit_id
                                ),
                              });
                            }
                          }}
                          className="h-4 w-4 accent-emerald-500 cursor-pointer"
                        />
                        <span className={`text-sm ${checked ? "text-emerald-300 font-medium" : "text-slate-300"}`}>
                          {bu.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {inviteBUError && (
                  <div
                    data-testid="invite-bu-error"
                    className="mt-2 text-xs text-rose-300"
                  >
                    {inviteBUError}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowInviteModal(false); setInviteError(null); setInviteBUError(null); setInviteSuccess(null); }}
                data-testid="invite-cancel-btn"
                className="flex-1 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviteSubmitting || !invite.email || !invite.operator_code || !invite.name}
                data-testid="invite-send-btn"
                className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
              >
                {inviteSubmitting ? "Sending…" : "Send Invite"}
              </button>
            </div>
            {inviteError && (
              <div
                data-testid="invite-error-message"
                className="mt-4 px-3 py-2 rounded-md border border-rose-500/40 bg-rose-500/10 text-xs text-rose-200"
              >
                {inviteError}
              </div>
            )}
            {inviteSuccess && (
              <div
                data-testid="invite-success-message"
                className="mt-4 px-3 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-xs text-emerald-200"
              >
                {inviteSuccess}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// ============================================================================
// SETTINGS PAGE (FOUNDER ONLY)
// ============================================================================

const SettingsPage = () => {
  const { user } = useAuth();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBUModal, setShowBUModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [newBU, setNewBU] = useState({ name: "", description: "" });
  const [newNetwork, setNewNetwork] = useState({
    name: "",
    business_unit_id: "",
    contact_name: "",
    contact_email: "",
  });
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaOAuthStatus, setMetaOAuthStatus] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({});
  const [notifLabels, setNotifLabels] = useState({});
  const [notifFounderLocked, setNotifFounderLocked] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState("");
  const [auditActionTypes, setAuditActionTypes] = useState([]);
  const [auditPage, setAuditPage] = useState(0);
  const AUDIT_PAGE_SIZE = 25;
  const [costThreshold, setCostThreshold] = useState(15);
  const [costThresholdInput, setCostThresholdInput] = useState("15");
  const [costThresholdSaving, setCostThresholdSaving] = useState(false);
  const [hardCap, setHardCap] = useState(25);
  const [hardCapInput, setHardCapInput] = useState("25");
  const [hardCapSaving, setHardCapSaving] = useState(false);
  const location = useLocation();

  const fetchMetaAccounts = useCallback(async () => {
    setMetaLoading(true);
    try {
      const res = await axios.get(`${API}/meta/accounts`);
      setMetaAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Failed to fetch Meta accounts:", err);
    } finally {
      setMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthResult = params.get("meta_oauth");
    if (oauthResult === "success") {
      const count = params.get("accounts") || "0";
      setMetaOAuthStatus({ type: "success", message: `Successfully connected ${count} Meta ad account(s)!` });
      fetchMetaAccounts();
      window.history.replaceState({}, "", "/settings");
    } else if (oauthResult === "error") {
      const msg = params.get("msg") || "Unknown error";
      setMetaOAuthStatus({ type: "error", message: `OAuth failed: ${decodeURIComponent(msg)}` });
      window.history.replaceState({}, "", "/settings");
    }
  }, [location.search, fetchMetaAccounts]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [buRes, netRes] = await Promise.all([
          axios.get(`${API}/business-units`),
          axios.get(`${API}/networks`),
        ]);
        setBusinessUnits(buRes.data);
        setNetworks(netRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchMetaAccounts();
    // Fetch notification preferences
    axios.get(`${API}/operators/me/notification-preferences`).then(res => {
      setNotifPrefs(res.data.preferences || {});
      setNotifLabels(res.data.alert_type_labels || {});
      setNotifFounderLocked(res.data.founder_locked || false);
    }).catch(() => {});
  }, [fetchMetaAccounts]);

  const handleNotifToggle = async (alertType) => {
    if (notifFounderLocked) return;
    const updated = { ...notifPrefs, [alertType]: !notifPrefs[alertType] };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await axios.put(`${API}/operators/me/notification-preferences`, updated);
    } catch (e) {
      console.error("Failed to update notification preferences:", e);
    } finally {
      setNotifSaving(false);
    }
  };

  const handleRestartOnboarding = async () => {
    try {
      await axios.post(`${API}/operators/me/onboarding/reset`);
      alert("Onboarding tour reset! Refresh the page to start the tour.");
    } catch (e) {
      console.error("Failed to reset onboarding:", e);
    }
  };

  const fetchAuditLogs = useCallback(async (page = 0, actionType = "") => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: AUDIT_PAGE_SIZE, offset: page * AUDIT_PAGE_SIZE });
      if (actionType) params.set("action_type", actionType);
      const res = await axios.get(`${API}/audit-log?${params}`);
      setAuditLogs(res.data.entries || []);
      setAuditTotal(res.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch audit logs:", e);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const fetchAuditActionTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-log/action-types`);
      setAuditActionTypes(res.data.action_types || []);
    } catch (e) {
      console.error("Failed to fetch audit action types:", e);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "FOUNDER") {
      fetchAuditLogs(0, "");
      fetchAuditActionTypes();
      // Fetch cost alert threshold
      axios.get(`${API}/cost-alert/threshold`).then(res => {
        setCostThreshold(res.data.threshold);
        setCostThresholdInput(String(res.data.threshold));
      }).catch(() => {});
      // Fetch circuit breaker hard cap
      axios.get(`${API}/circuit-breaker/hard-cap`).then(res => {
        setHardCap(res.data.hard_cap);
        setHardCapInput(String(res.data.hard_cap));
      }).catch(() => {});
    }
  }, [user, fetchAuditLogs, fetchAuditActionTypes]);

  const handleSaveCostThreshold = async () => {
    setCostThresholdSaving(true);
    try {
      const val = parseFloat(costThresholdInput) || 0;
      await axios.put(`${API}/cost-alert/threshold`, { threshold: val });
      setCostThreshold(val);
    } catch (e) {
      console.error("Failed to save cost threshold:", e);
    } finally {
      setCostThresholdSaving(false);
    }
  };

  const handleSaveHardCap = async () => {
    setHardCapSaving(true);
    try {
      const val = parseFloat(hardCapInput) || 0;
      await axios.put(`${API}/circuit-breaker/hard-cap`, { hard_cap: val });
      setHardCap(val);
    } catch (e) {
      console.error("Failed to save hard cap:", e);
    } finally {
      setHardCapSaving(false);
    }
  };

  const handleAuditFilterChange = (val) => {
    setAuditFilter(val);
    setAuditPage(0);
    fetchAuditLogs(0, val);
  };

  const handleAuditPageChange = (newPage) => {
    setAuditPage(newPage);
    fetchAuditLogs(newPage, auditFilter);
  };

  const handleCreateBU = async () => {
    try {
      const response = await axios.post(`${API}/business-units`, newBU);
      setBusinessUnits([...businessUnits, response.data]);
      setShowBUModal(false);
      setNewBU({ name: "", description: "" });
    } catch (error) {
      console.error("Failed to create BU:", error);
      alert(error.response?.data?.detail || "Failed to create business unit");
    }
  };

  const handleCreateNetwork = async () => {
    try {
      const response = await axios.post(`${API}/networks`, newNetwork);
      setNetworks([...networks, response.data]);
      setShowNetworkModal(false);
      setNewNetwork({ name: "", business_unit_id: "", contact_name: "", contact_email: "" });
    } catch (error) {
      console.error("Failed to create network:", error);
      alert(error.response?.data?.detail || "Failed to create network");
    }
  };

  const handleConnectMeta = () => {
    window.location.href = `${API}/meta/oauth/login`;
  };

  const handleDisconnectMeta = async (metaAccountId) => {
    if (!window.confirm(`Disconnect account ${metaAccountId}?`)) return;
    try {
      await axios.delete(`${API}/meta/accounts/${metaAccountId}`);
      setMetaAccounts(metaAccounts.filter(a => a.meta_account_id !== metaAccountId));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to disconnect");
    }
  };

  const handleRefreshTokens = async () => {
    try {
      const res = await axios.post(`${API}/meta/accounts/refresh`);
      alert(`Refreshed: ${res.data.refreshed}, Failed: ${res.data.failed}`);
      fetchMetaAccounts();
    } catch (err) {
      alert(err.response?.data?.detail || "Refresh failed");
    }
  };

  // Settings is now accessible to OPERATOR / MEDIA_BUYER for the Meta-account
  // connection card. Every other section (Business Units, Networks,
  // Notification Preferences, Onboarding, Cost / Hard-Cap, Audit Log, Tiny
  // Tim) is wrapped in an isFounder guard below so non-founders only see the
  // Meta connection block.
  const isFounder = user?.role === "FOUNDER";

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="settings-title">
            Settings
          </h2>
          <p className="text-slate-400 mt-1">
            {isFounder
              ? "Configure business units, networks, and ad account connections"
              : "Connect your Meta ad accounts to run campaigns"}
          </p>
        </div>

        {/* Meta OAuth Status Banner */}
        {metaOAuthStatus && (
          <div
            data-testid="meta-oauth-status"
            className={`p-4 rounded-xl border ${metaOAuthStatus.type === "success"
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
              : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            <div className="flex items-center justify-between">
              <p>{metaOAuthStatus.message}</p>
              <button onClick={() => setMetaOAuthStatus(null)} className="text-slate-400 hover:text-white ml-4">&times;</button>
            </div>
          </div>
        )}

        {/* Connected Meta Ad Accounts */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="meta-accounts-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Connected Meta Ad Accounts</h3>
              <p className="text-slate-500 text-sm mt-1">Connect your Meta Business ad accounts via OAuth</p>
            </div>
            <div className="flex items-center gap-3">
              {user?.role === "FOUNDER" && metaAccounts.length > 0 && (
                <button
                  onClick={handleRefreshTokens}
                  data-testid="refresh-tokens-btn"
                  className="px-3 py-1.5 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded-lg text-sm transition-colors"
                >
                  Refresh Tokens
                </button>
              )}
              <button
                onClick={handleConnectMeta}
                data-testid="connect-meta-btn"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/></svg>
                Connect Meta Account
              </button>
            </div>
          </div>

          {metaLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : metaAccounts.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-700 rounded-lg">
              <svg className="w-10 h-10 mx-auto text-slate-600 mb-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/></svg>
              <p className="text-slate-500 text-sm">No Meta ad accounts connected yet</p>
              <p className="text-slate-600 text-xs mt-1">Click "Connect Meta Account" to get started</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="meta-accounts-list">
              {metaAccounts.map((acct) => {
                const isExpired = acct.token_expires_at && new Date(acct.token_expires_at) < new Date();
                const daysLeft = acct.token_expires_at
                  ? Math.max(0, Math.ceil((new Date(acct.token_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
                  : null;
                return (
                  <div key={acct.meta_account_id} className="p-4 bg-slate-800/50 rounded-lg flex items-center justify-between" data-testid={`meta-account-${acct.meta_account_id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{acct.account_name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          acct.account_status === "ACTIVE" ? "bg-emerald-500/20 text-emerald-400" :
                          acct.account_status === "DISABLED" ? "bg-red-500/20 text-red-400" :
                          "bg-purple-500/20 text-purple-400"
                        }`}>{acct.account_status}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span>{acct.meta_account_id}</span>
                        <span>{acct.currency}</span>
                        {acct.timezone_name && <span>{acct.timezone_name}</span>}
                        {user?.role === "FOUNDER" && acct.operator_name && (
                          <span className="text-blue-400">by {acct.operator_name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        {isExpired ? (
                          <span className="text-red-400">Token expired</span>
                        ) : daysLeft !== null ? (
                          <span className={daysLeft < 7 ? "text-purple-400" : "text-slate-500"}>
                            Token expires in {daysLeft}d
                          </span>
                        ) : null}
                        {acct.last_synced && (
                          <span className="text-slate-600">Last synced: {new Date(acct.last_synced).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDisconnectMeta(acct.meta_account_id)}
                      data-testid={`disconnect-${acct.meta_account_id}`}
                      className="px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors"
                    >
                      Disconnect
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {isFounder && (loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Business Units */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Business Units</h3>
                <button
                  onClick={() => setShowBUModal(true)}
                  data-testid="create-bu-btn"
                  className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-sm transition-colors"
                >
                  Add
                </button>
              </div>
              {businessUnits.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No business units yet</p>
              ) : (
                <div className="space-y-3">
                  {businessUnits.map((bu) => (
                    <div key={bu.business_unit_id} className="p-4 bg-slate-800/50 rounded-lg">
                      <p className="text-white font-medium">{bu.name}</p>
                      {bu.description && (
                        <p className="text-slate-400 text-sm mt-1">{bu.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Networks */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Networks</h3>
                <button
                  onClick={() => setShowNetworkModal(true)}
                  data-testid="create-network-btn"
                  disabled={businessUnits.length === 0}
                  className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm transition-colors"
                >
                  Add
                </button>
              </div>
              {networks.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No networks yet</p>
              ) : (
                <div className="space-y-3">
                  {networks.map((net) => {
                    const bu = businessUnits.find(
                      (b) => b.business_unit_id === net.business_unit_id
                    );
                    return (
                      <div key={net.network_id} className="p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-white font-medium">{net.name}</p>
                        <p className="text-slate-400 text-sm mt-1">
                          {bu?.name || "Unknown BU"}
                          {net.contact_name && ` • ${net.contact_name}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create BU Modal */}
      {showBUModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Create Business Unit</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Name (e.g., AFFILIATE, ABH)
                </label>
                <input
                  type="text"
                  value={newBU.name}
                  onChange={(e) => setNewBU({ ...newBU, name: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white uppercase focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Description</label>
                <textarea
                  value={newBU.description}
                  onChange={(e) => setNewBU({ ...newBU, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowBUModal(false)}
                className="flex-1 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBU}
                disabled={!newBU.name}
                className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Network Modal */}
      {showNetworkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Create Network</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Name</label>
                <input
                  type="text"
                  value={newNetwork.name}
                  onChange={(e) => setNewNetwork({ ...newNetwork, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Business Unit
                </label>
                <select
                  value={newNetwork.business_unit_id}
                  onChange={(e) =>
                    setNewNetwork({ ...newNetwork, business_unit_id: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select...</option>
                  {businessUnits.map((bu) => (
                    <option key={bu.business_unit_id} value={bu.business_unit_id}>
                      {bu.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={newNetwork.contact_name}
                  onChange={(e) => setNewNetwork({ ...newNetwork, contact_name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={newNetwork.contact_email}
                  onChange={(e) => setNewNetwork({ ...newNetwork, contact_email: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowNetworkModal(false)}
                className="flex-1 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNetwork}
                disabled={!newNetwork.name || !newNetwork.business_unit_id}
                className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Preferences Section — Founder only */}
      {isFounder && (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="notification-preferences-section">
        <h3 className="text-lg font-bold text-white mb-4">Notification Preferences</h3>
        {notifFounderLocked && (
          <p className="text-sm text-amber-400 mb-4">Founder always receives all alerts — preferences are locked.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(notifPrefs).map(([alertType, enabled]) => (
            <div key={alertType} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
              <span className="text-sm text-white">{notifLabels[alertType] || alertType}</span>
              <button
                onClick={() => handleNotifToggle(alertType)}
                disabled={notifFounderLocked || notifSaving}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  enabled ? 'bg-emerald-500' : 'bg-slate-600'
                } ${notifFounderLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid={`notif-toggle-${alertType}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  enabled ? 'left-5' : 'left-0.5'
                }`}></span>
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Onboarding Section — Founder only */}
      {isFounder && (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="onboarding-settings-section">
        <h3 className="text-lg font-bold text-white mb-2">Onboarding Tour</h3>
        <p className="text-sm text-slate-400 mb-4">Re-trigger the guided tour that introduces key areas of the system.</p>
        <button
          onClick={handleRestartOnboarding}
          className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg text-sm hover:bg-slate-700 transition-colors"
          data-testid="restart-onboarding-btn"
        >
          Restart Onboarding Tour
        </button>
      </div>
      )}

      {/* Cost Alert Threshold — Founder only */}
      {user?.role === "FOUNDER" && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="cost-alert-section">
          <h3 className="text-lg font-bold text-white mb-1">Cost Alert Threshold</h3>
          <p className="text-sm text-slate-400 mb-4">
            Receive a Telegram alert when daily Anthropic API spend exceeds this amount. Set to 0 to disable.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm">$</span>
            <input
              type="number"
              step="0.5"
              min="0"
              value={costThresholdInput}
              onChange={(e) => setCostThresholdInput(e.target.value)}
              className="w-28 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-emerald-500 focus:outline-none"
              data-testid="cost-threshold-input"
            />
            <span className="text-slate-500 text-xs">/ day</span>
            <button
              onClick={handleSaveCostThreshold}
              disabled={costThresholdSaving || parseFloat(costThresholdInput) === costThreshold}
              className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg disabled:opacity-40 transition-colors"
              data-testid="save-cost-threshold-btn"
            >
              {costThresholdSaving ? "Saving..." : "Save"}
            </button>
          </div>
          {costThreshold === 0 && (
            <p className="text-xs text-amber-400/80 mt-2">Cost alerts are currently disabled</p>
          )}
        </div>
      )}

      {/* Circuit Breaker Hard Cap — Founder only */}
      {user?.role === "FOUNDER" && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="hard-cap-section">
          <h3 className="text-lg font-bold text-white mb-1">Claude API Hard Cap (Circuit Breaker)</h3>
          <p className="text-sm text-slate-400 mb-4">
            Auto-pause non-critical Claude calls when daily spend exceeds this amount. Creative Engine chat and critical monitoring are never paused. Resets at midnight UTC. Set to 0 to disable.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm">$</span>
            <input
              type="number"
              step="1"
              min="0"
              value={hardCapInput}
              onChange={(e) => setHardCapInput(e.target.value)}
              className="w-28 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-red-500 focus:outline-none"
              data-testid="hard-cap-input"
            />
            <span className="text-slate-500 text-xs">/ day</span>
            <button
              onClick={handleSaveHardCap}
              disabled={hardCapSaving || parseFloat(hardCapInput) === hardCap}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg disabled:opacity-40 transition-colors"
              data-testid="save-hard-cap-btn"
            >
              {hardCapSaving ? "Saving..." : "Save"}
            </button>
          </div>
          {hardCap === 0 && (
            <p className="text-xs text-amber-400/80 mt-2">Circuit breaker is currently disabled &mdash; no spend cap enforced</p>
          )}
        </div>
      )}

      {/* Audit Log Section — Founder only */}
      {user?.role === "FOUNDER" && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="audit-log-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">Audit Log</h3>
              <p className="text-sm text-slate-400 mt-1">System-wide record of significant actions</p>
            </div>
            <select
              value={auditFilter}
              onChange={(e) => handleAuditFilterChange(e.target.value)}
              className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-emerald-500 focus:outline-none w-full sm:w-48"
              data-testid="audit-filter-select"
            >
              <option value="">All Actions</option>
              {auditActionTypes.map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>

          {auditLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
          ) : auditLogs.length === 0 ? (
            <p className="text-slate-500 text-center py-8 text-sm">No audit log entries yet</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-log-table">
                  <thead>
                    <tr className="border-b border-slate-700/50">
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Timestamp</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Action</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium hidden sm:table-cell">Operator</th>
                      <th className="text-left py-2 px-3 text-slate-400 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((entry, i) => (
                      <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="py-2.5 px-3 text-slate-400 text-xs whitespace-nowrap">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700/50 text-slate-300 whitespace-nowrap">
                            {(entry.action_type || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-300 text-xs hidden sm:table-cell">{entry.operator_name || '—'}</td>
                        <td className="py-2.5 px-3 text-slate-300 text-xs max-w-xs truncate">{entry.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800/50">
                <p className="text-xs text-slate-500">
                  Showing {auditPage * AUDIT_PAGE_SIZE + 1}–{Math.min((auditPage + 1) * AUDIT_PAGE_SIZE, auditTotal)} of {auditTotal}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAuditPageChange(auditPage - 1)}
                    disabled={auditPage === 0}
                    className="px-3 py-1 text-xs rounded-lg bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-30 hover:border-slate-600 transition-colors"
                    data-testid="audit-prev-page"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => handleAuditPageChange(auditPage + 1)}
                    disabled={(auditPage + 1) * AUDIT_PAGE_SIZE >= auditTotal}
                    className="px-3 py-1 text-xs rounded-lg bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-30 hover:border-slate-600 transition-colors"
                    data-testid="audit-next-page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tiny Tim Settings — Founder only */}
      {user?.role === "FOUNDER" && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mt-8" data-testid="tiny-tim-settings-section">
          <TinyTimSettings />
        </div>
      )}

    </DashboardLayout>
  );
};

// ============================================================================
// CREATIVE ENGINE WRAPPER
// ============================================================================

// Affiliate Creative landing (flag ON). Presents the two entry points and
// routes into the EXISTING workflows — it does not reimplement either one.
// A ?session= deep-link (offer "New Creative Session", or a refresh) goes
// straight into the Shared CCS. /ccs is never exposed as a URL.
const CreativeLanding = () => {
  const [params] = useSearchParams();
  const hasCcsSession = !!params.get("session");
  const [mode, setMode] = useState(hasCcsSession ? "custom" : null);

  if (hasCcsSession || mode === "custom") return <CustomCreativeSession />;
  if (mode === "autonomous") return <CreativeHub />;

  return (
    <div className="max-w-3xl mx-auto mt-10 px-4" data-testid="creative-landing">
      <h2 className="text-xl font-bold text-white">Creative</h2>
      <p className="text-slate-400 text-sm mt-1">Choose how you want to build this creative.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <button
          type="button"
          onClick={() => setMode("autonomous")}
          className="text-left bg-slate-900/50 border border-slate-800 hover:border-emerald-600 rounded-xl p-5 transition-colors"
          data-testid="creative-choice-autonomous"
        >
          <div className="text-white font-semibold">Autonomous Creative</div>
          <p className="text-slate-400 text-sm mt-1">
            Cipher generates the creative for you through the guided workflow
            (psychology → angles → hooks → scripts → production).
          </p>
        </button>
        <button
          type="button"
          onClick={() => setMode("custom")}
          className="text-left bg-slate-900/50 border border-slate-800 hover:border-emerald-600 rounded-xl p-5 transition-colors"
          data-testid="creative-choice-custom"
        >
          <div className="text-white font-semibold">Custom Creative Session</div>
          <p className="text-slate-400 text-sm mt-1">
            You author the hook, script and CTA yourself, with Claude as a
            creative collaborator on the side.
          </p>
        </button>
      </div>
    </div>
  );
};

const CreativeEngineWrapper = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse session ID from query params once per mount so re-renders
  // don't allocate a fresh URLSearchParams or pass stale session IDs
  // into the creative engine.
  const sessionId = useMemo(() => {
    return new URLSearchParams(window.location.search).get('session');
  }, []);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const response = await axios.get(`${API}/offers/${offerId}`);
        setOffer(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load offer");
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [offerId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !offer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error || "Offer not found"}</p>
          <button
            onClick={() => navigate("/offers")}
            className="text-emerald-400 hover:text-emerald-300"
          >
            Back to Offers
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <CreativeEnginePage 
        offer={offer} 
        onClose={() => navigate(`/offers/${offerId}`)}
        sessionId={sessionId}
      />
    </DashboardLayout>
  );
};

// ============================================================================
// GHOST RECON WRAPPERS
// ============================================================================

const GhostReconDashboardPage = () => {
  return (
    <DashboardLayout>
      <ErrorBoundary fallbackMessage="Something went wrong loading Ghost Recon. Please refresh.">
        <GhostReconRebuilding />
      </ErrorBoundary>
    </DashboardLayout>
  );
};

const GhostReconOfferDetailPage = () => {
  return (
    <DashboardLayout>
      <GhostReconOfferDetail />
    </DashboardLayout>
  );
};

// ============================================================================
// APP ROUTER
// ============================================================================

function BUProviderBridge({ children }) {
  const { user, loading } = useAuth();
  // Wait for auth to resolve before mounting BUProvider so its useState
  // initializer runs against the real user's business_unit_access (prevents
  // a race where multi-BU operators were silently auto-selected to AFFILIATE).
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div
          data-testid="bu-provider-loading"
          className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"
        />
      </div>
    );
  }
  return <BUProvider user={user}>{children}</BUProvider>;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <OnboardingTour user={user} />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Executive Brain (flag-gated, founder-only). ONE persistent immersive surface;
          NOT wrapped in DashboardLayout (keeps Cipher nav reachable via its own way-back).
          Off => not registered, existing app byte-identical. Deep-link focus route parity. */}
      {EXEC_BRAIN_ENABLED && (
        <>
          <Route path="/brain" element={<ProtectedRoute requireFounder><BrainDashboardLayout user={user}><BrainOverview user={user} /></BrainDashboardLayout></ProtectedRoute>} />
          <Route path="/brain/decisions" element={<ProtectedRoute requireFounder><BrainDashboardLayout user={user}><BrainOverview user={user} /></BrainDashboardLayout></ProtectedRoute>} />
          <Route path="/brain/decisions/:decisionId" element={<ProtectedRoute requireFounder><BrainDashboardLayout user={user}><DecisionReview /></BrainDashboardLayout></ProtectedRoute>} />
          <Route path="/brain/memory" element={<ProtectedRoute requireFounder><BrainDashboardLayout user={user}><MemoryScreen /></BrainDashboardLayout></ProtectedRoute>} />
          <Route path="/brain/operations" element={<ProtectedRoute requireFounder><BrainDashboardLayout user={user}><OperationsScreen /></BrainDashboardLayout></ProtectedRoute>} />
          {/* Living Canvas — the immersive neural-graph surface. NOT wrapped in
              BrainDashboardLayout: BrainSurface is its own full-screen field with
              its own "← Cipher" way-back, per its file-level comment. */}
          <Route path="/brain/living" element={<ProtectedRoute requireFounder><BrainSurface /></ProtectedRoute>} />
        </>
      )}
      {V2_ENABLED && <Route path="/v2/*" element={<V2Routes />} />}
      <Route
        path="/select-bu"
        element={
          <ProtectedRoute>
            <BUSelectorScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence/experiments"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ExperimentsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence/observations"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ObservationsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence/hypotheses"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><HypothesesPage user={user} /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intelligence/veo-queue"
        element={
          <ProtectedRoute>
            <DashboardLayout><VeoQueuePage user={user} /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <PipelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers"
        element={
          <ProtectedRoute requireFounderOrMediaBuyer>
            <OffersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/:offerId"
        element={
          <ProtectedRoute requireFounder>
            <OfferDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creative-engine/:offerId"
        element={
          <ProtectedRoute>
            <CreativeEngineWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creative/image"
        element={
          <ProtectedRoute>
            <DashboardLayout><ImageAdStudio /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creative/image/:offerId"
        element={
          <ProtectedRoute>
            <DashboardLayout><ImageAdStudio /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creative"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              {process.env.REACT_APP_CCS_ENABLED === "true"
                ? <CreativeLanding />
                : <CreativeHub />}
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ghost-recon"
        element={
          <ProtectedRoute requireFounderOrBuyer>
            <GhostReconDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ghost-recon/:offerId"
        element={
          <ProtectedRoute requireFounderOrBuyer>
            <GhostReconOfferDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creatives/:creativeId/history"
        element={
          <ProtectedRoute requireFounderOrBuyer>
            <DashboardLayout><CreativeHistory /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* ── /productions/:productionId/prompts — RETIRED 2026-09-08 ──────────
          V2-CREATIVE-WAVE1, on the Founder's ruling: "Retire the prompt
          approval screen along with Veo."

          This screen approved generated Veo prompts before submission to
          Google. Veo is retired (docs/REFERENCE.md, standing decisions) and
          nothing submits to it, so the gate guards a door that no longer opens.
          `CREATIVE_V2_INVENTORY.md` §5 reached the same verdict independently:
          "goes with Veo", not ported to v2.

          RULE 8 — what was checked before removing the route:
            bare name `AIPromptReview`   — this import and this route only, in
                                           product code; the rest are documents
            the component file           — KEPT, with a tombstone header. It is
                                           the only record of what those six
                                           endpoints were for, and a wrong
                                           retention costs nothing
            its 6 backend endpoints      — `routers/ai_prompt_review.py`, KEPT.
                                           They are now called by no frontend
                                           file; removing a live router because
                                           its one caller went away is the
                                           deletion rule 8 exists to stop.
                                           REPORTED for a separate ruling
            string dispatch / templates  — none; the path is a literal here
            callers outside this repo    — none known; it is a UI route, not an
                                           API

          The route is gone, so the screen is unreachable. That is the
          retirement. Deleting the file and the router is a further step and is
          the Founder's to take.
      ────────────────────────────────────────────────────────────────────── */}
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-health"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><CampaignHealthPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pnl"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><PnlDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operators"
        element={
          <ProtectedRoute requireFounder>
            <OperatorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/predictions"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><PredictionsDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><OperationsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh-engine"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHCreativeEngine /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-day"
        element={
          <ProtectedRoute>
            <DashboardLayout><MyDayPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recommendations"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><RecommendationsDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/velocity"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><VelocityDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creative-intel"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><CreativeIntelligenceDashboard /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh/attribution"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHAttributionPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh/audience"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHAudiencePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh/ab-tests"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHABTestsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh/ab-tests/:testId"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHABTestsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/abh/email"
        element={
          <ProtectedRoute requireFounder>
            <DashboardLayout><ABHEmailPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/analyzer"
        element={
          <ProtectedRoute>
            <DashboardLayout><CreativeAnalyzer /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="*" element={
          <AuthProvider>
            <BUProviderBridge>
              <TinyTimContextProvider>
                <AppRouter />
              </TinyTimContextProvider>
            </BUProviderBridge>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
